import axios from "axios";
import type { AgendaMarcarResposta, AgendaRemoverResposta, AgendaDiagnostico } from "../components/licitacoes/agendaGoogle";
import { avisarSeGravouAssinatura } from "../utils/avisoDeGravacao";
import { corpoDoScrape } from "../utils/linkDoPortal";
import type { RateioDoGrupo } from "../utils/rateioDoGrupo";
import type { DeclaracaoDeCompetenciaPassada, EstadoDoMotor, ResumoDoDryRun } from "../utils/motorDoRateio";
import type { SimulacaoDaDivisao } from "../utils/redividir";
import type { JobDaLista } from "../utils/jobDeAutomacao";
import {
  AUTH_BASE,
  ensureFreshToken,
  invalidateAccessToken,
  setSessionToken,
  clearSession,
} from "./authTokens";

import { prazoDaRequisicao, PRAZO_DE_ARQUIVO_MS } from "./prazoDaRequisicao";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

const api = axios.create({
  baseURL: API_URL,
  headers: { "Content-Type": "application/json" },
  // Nenhum cookie é usado para falar com o backend: a sessão viaja no header
  // Bearer, e o único cookie que existe (o refresh) é first-party do BFF e só
  // vale para /api/auth no host do front.
  withCredentials: false,
});

/** Rotas de auth: vão para o BFF same-origin, sem Bearer e sem refresh automático. */
const isAuthRoute = (url?: string) => !!url && url.startsWith("/auth/");

// Request interceptor: garante um access token válido antes de cada chamada.
// Como a renovação é proativa e serializada entre abas, na prática o 401 deixa
// de acontecer no fluxo normal.
api.interceptors.request.use(async (config) => {
  // Prazo: leitura 60 s, escrita 10 min, ou o que a chamada declarou (services/prazoDaRequisicao).
  config.timeout = prazoDaRequisicao(config.method, config.timeout);
  if (isAuthRoute(config.url)) return config;
  try {
    const token = await ensureFreshToken();
    config.headers.Authorization = `Bearer ${token}`;
  } catch {
    // Sessão perdida: deixa a requisição seguir e falhar com 401, que a UI já
    // sabe tratar. Bloquear aqui esconderia o erro do usuário.
  }
  return config;
});

api.interceptors.response.use(
  (response) => {
    // Assinatura gravada → quem guarda leitura derivada dela (rateio do grupo)
    // busca de novo. Ver utils/avisoDeGravacao.ts.
    avisarSeGravouAssinatura(response.config?.method, response.config?.url);
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Retry on 429 (Too Many Requests) with exponential backoff
    if (error.response?.status === 429 && !originalRequest._retryCount) {
      originalRequest._retryCount = 0;
    }
    if (
      error.response?.status === 429 &&
      (originalRequest._retryCount || 0) < 3
    ) {
      originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;
      const delay = Math.pow(2, originalRequest._retryCount) * 1000; // 2s, 4s, 8s
      await new Promise((resolve) => setTimeout(resolve, delay));
      return api(originalRequest);
    }

    // 401 fora das rotas de auth: força uma renovação e repete UMA vez.
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isAuthRoute(originalRequest.url)
    ) {
      originalRequest._retry = true;
      invalidateAccessToken();
      try {
        const token = await ensureFreshToken(true);
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      } catch {
        // ensureFreshToken já limpou a sessão e notificou a UI.
      }
    }

    return Promise.reject(error);
  },
);

/**
 * Instância separada para as rotas de autenticação.
 *
 * Aponta para o BFF (same-origin em produção), e é a ÚNICA que manda cookie:
 * o refresh token é um cookie httpOnly first-party com Path=/api/auth. Todo o
 * resto da API é falado direto com o EC2, via Bearer, sem cookie nenhum.
 */
const authApi = axios.create({
  baseURL: AUTH_BASE,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

/** Rotas de auth que NÃO exigem um access token válido. */
const PUBLIC_AUTH_ROUTES = [
  "/login",
  "/refresh",
  "/logout",
  "/register",
  "/mfa/verify",
  "/webauthn/authenticate/begin",
  "/webauthn/authenticate/complete",
  "/forgot-password",
  "/reset-password",
];

authApi.interceptors.request.use(async (config) => {
  if (PUBLIC_AUTH_ROUTES.some((route) => config.url?.startsWith(route)))
    return config;
  // Authorization já posto pelo chamador vence: é o caso do token de escopo
  // `mfa_setup` emitido pelo login quando o usuário ainda não tem 2º fator.
  // Nesse momento NÃO existe sessão, e chamar ensureFreshToken aqui estouraria
  // com SessionExpiredError — que dispara o onSessionLost e joga o usuário de
  // volta ao login no meio do cadastro do TOTP.
  if (config.headers.Authorization) return config;
  try {
    config.headers.Authorization = `Bearer ${await ensureFreshToken()}`;
  } catch {
    /* deixa falhar com 401, tratado pela UI */
  }
  return config;
});

/** Guarda o par recém-emitido pelo login. O refresh já veio como cookie httpOnly. */
const adoptSession = (data: any) => {
  if (data?.access_token) setSessionToken(data.access_token, data.id);
  return data;
};

/** Header do token de escopo, que o interceptor do authApi deixa passar. */
const scopedAuth = (token: string) => ({
  headers: { Authorization: `Bearer ${token}` },
});

export const authService = {
  login: async (email: string, password: string, turnstileToken?: string) => {
    const data = (
      await authApi.post("/login", { email, password, turnstileToken })
    ).data;
    // MFA pendente: ainda não há sessão para adotar.
    if (data.mfaRequired) return data;
    return adoptSession(data);
  },
  verifyMfa: async (mfaToken: string, code: string, rememberDevice = false) =>
    adoptSession(
      (await authApi.post("/mfa/verify", { mfaToken, code, rememberDevice }))
        .data,
    ),
  getMfaStatus: async () => (await authApi.get("/mfa/status")).data,
  /**
   * `mfaSetupToken` é o token de escopo do gate de login (DEM-95): vale só para
   * /mfa/setup e /mfa/setup/verify, por 10 min. Sem ele a chamada usa a sessão
   * normal — que é o "ativar MFA" das Configurações.
   */
  setupMfa: async (mfaSetupToken?: string) =>
    (
      await authApi.post(
        "/mfa/setup",
        {},
        mfaSetupToken ? scopedAuth(mfaSetupToken) : undefined,
      )
    ).data,
  /**
   * Dois tokens distintos: `setupToken` (body) carrega o segredo TOTP devolvido
   * pelo /mfa/setup; `mfaSetupToken` (Bearer) é a identidade do gate.
   * No sucesso o backend devolve a sessão completa — adotada aqui para o
   * usuário não ter que logar de novo logo após cadastrar o 2º fator.
   */
  verifyMfaSetup: async (
    setupToken: string,
    code: string,
    mfaSetupToken?: string,
  ) =>
    adoptSession(
      (
        await authApi.post(
          "/mfa/setup/verify",
          { setupToken, code },
          mfaSetupToken ? scopedAuth(mfaSetupToken) : undefined,
        )
      ).data,
    ),
  disableMfa: async (code: string) =>
    (await authApi.post("/mfa/disable", { code })).data,
  adminDisableMfa: async (userId: number) =>
    (await authApi.post(`/mfa/admin-disable/${userId}`)).data,
  getMe: async () => (await authApi.get("/me")).data,
  refresh: async () => (await authApi.post("/refresh")).data,
  logout: async () => {
    try {
      return (await authApi.post("/logout", {})).data;
    } finally {
      clearSession();
    }
  },
  changePassword: async (data: {
    currentPassword: string;
    newPassword: string;
  }) => (await authApi.post("/change-password", data)).data,
  forgotPassword: async (email: string) =>
    (await authApi.post("/forgot-password", { email })).data,
  resetPassword: async (token: string, newPassword: string) =>
    (await authApi.post("/reset-password", { token, newPassword })).data,
  // Sessões ativas (uma por família de refresh token)
  listSessions: async () => (await authApi.get("/sessions")).data,
  revokeSession: async (id: string) =>
    (await authApi.delete(`/sessions/${id}`)).data,
  revokeOtherSessions: async () => (await authApi.delete("/sessions")).data,
  // Dispositivos confiáveis (MFA "lembrar este dispositivo")
  listTrustedDevices: async () => (await authApi.get("/devices")).data,
  revokeTrustedDevice: async (id: number) =>
    (await authApi.delete(`/devices/${id}`)).data,
  revokeAllTrustedDevices: async () => (await authApi.delete("/devices")).data,
  // WebAuthn / Passkeys
  webauthnRegisterBegin: async () =>
    (await authApi.post("/webauthn/register/begin")).data,
  webauthnRegisterComplete: async (credential: string, deviceName?: string) =>
    (
      await authApi.post("/webauthn/register/complete", {
        credential,
        deviceName,
      })
    ).data,
  webauthnListCredentials: async () =>
    (await authApi.get("/webauthn/credentials")).data,
  webauthnDeleteCredential: async (id: number) =>
    (await authApi.delete(`/webauthn/credentials/${id}`)).data,
  // email é opcional: sem ele o backend emite desafio sem `allowCredentials` e o
  // navegador oferece as passkeys que já tem para este site. Com ele, mantém o
  // fluxo antigo como fallback.
  webauthnAuthenticateBegin: async (email?: string) =>
    (await authApi.post("/webauthn/authenticate/begin", email ? { email } : {}))
      .data,
  webauthnAuthenticateComplete: async (
    email: string | undefined,
    credential: string,
  ) =>
    adoptSession(
      (
        await authApi.post(
          "/webauthn/authenticate/complete",
          email ? { email, credential } : { credential },
        )
      ).data,
    ),
};

export const productsService = {
  getAll: async () => (await api.get("/products")).data,
  create: async (data: any) => (await api.post("/products", data)).data,
  update: async (id: number, data: any) =>
    (await api.put("/products/" + id, data)).data,
  delete: async (id: number) => (await api.delete("/products/" + id)).data,
  /** Substitui as faixas de preço por volume do produto (lista vazia = sem faixa). */
  updatePriceTiers: async (
    id: number,
    tiers: Array<{ minQuantity: number; unitPrice: number }>,
  ) => (await api.put("/products/" + id + "/price-tiers", { tiers })).data,
};

export interface QuoteLine {
  productId: number;
  code: string;
  name: string;
  type: "MRR" | "PS";
  quantity: number;
  unitPrice: number;
  subtotal: number;
  /**
   * A faixa que PRECIFICOU, não a que a quantidade alcança. Desde a regra
   * monotônica (backend #231) quem tem 190 é cobrado como 191 porque sai mais
   * barato, e então a faixa acesa é a de 191+ — mostrar a de 41–190 faria o
   * documento contradizer o próprio número.
   */
  tier: { minQuantity: number; maxQuantity: number | null; label: string } | null;
  belowMinimum: boolean;
  /**
   * Quantidade que entrou na conta, presente SÓ quando maior que a contratada.
   * É o que impede a tela de multiplicar quantidade × unitário e mostrar menos
   * do que a fatura cobra.
   */
  billedQuantity?: number;
}
export interface Quote {
  lines: QuoteLine[];
  mrrSubtotal: number;
  positionsSubtotal: number;
  floor: number;
  floorAdjustment: number;
  mrr: number;
  oneTime: number;
  enterprise: boolean;
  notes: string[];
}
export type BriefingBloco = "descoberta" | "oferta" | "dimensionamento" | "provas" | "condicoes" | "resultado";
export interface BriefingPendencia { etapa: 1 | 2 | 3; bloco: BriefingBloco; campo: string; rotulo: string }
export interface Briefing {
  id: number | null;
  leadId: number;
  proposalId?: number | null;
  descoberta: Record<string, any>;
  oferta: Record<string, any>;
  dimensionamento: Record<string, any>;
  provas: Record<string, any>;
  condicoes: Record<string, any>;
  resultado: Record<string, any>;
  updatedAt?: string;
  pendencias: BriefingPendencia[];
  etapa1Completa: boolean;
  etapa2Completa: boolean;
  prontoParaGerar: boolean;
}
/** Briefing de proposta do lead (3 etapas). Pendências vêm calculadas do backend. */
export const briefingsService = {
  get: async (leadId: number): Promise<Briefing> =>
    (await api.get("/leads/" + leadId + "/briefing")).data,
  /** Cada bloco enviado substitui o bloco gravado; bloco ausente não é tocado. */
  save: async (leadId: number, blocos: Partial<Record<BriefingBloco, Record<string, any>>>): Promise<Briefing> =>
    (await api.put("/leads/" + leadId + "/briefing", blocos)).data,
};

export interface Rulebook { id: number; version: string; status: "rascunho" | "ativa" | "arquivada"; content: string; changelog: string | null; effectiveFrom: string | null; createdAt: string; updatedAt: string }
export interface ReferenceAccount {
  id: number; name: string; segment: string | null; city: string | null;
  status: "autorizado" | "bloqueado" | "pendente";
  allowName: boolean; allowLogo: boolean; allowTestimonial: boolean;
  testimonialText: string | null; testimonialAuthor: string | null; logoS3Key: string | null;
  isPreviousProposal: boolean; notes: string | null; decidedBy: string | null; decidedAt: string | null;
}
export interface ForbiddenTerm { id: number; pattern: string; reason: string; severity: "bloqueia" | "aviso"; scope: "proposta" | "social" | "ambos"; active: boolean }
export interface TextCheckHit { kind: "termo" | "cliente"; severity: "bloqueia" | "aviso"; match: string; reason: string; termId?: number; referenceId?: number }
/** Núcleo de propostas: regras versionadas, provas autorizadas, termos proibidos (só ADMIN escreve). */
export const proposalCoreService = {
  listRulebooks: async (): Promise<Rulebook[]> => (await api.get("/proposal-core/rulebooks")).data,
  activeRulebook: async (): Promise<Rulebook | null> => (await api.get("/proposal-core/rulebooks/active")).data || null,
  createRulebook: async (data: { version: string; content: string; changelog?: string; effectiveFrom?: string }): Promise<Rulebook> =>
    (await api.post("/proposal-core/rulebooks", data)).data,
  updateRulebook: async (id: number, data: { content?: string; changelog?: string; effectiveFrom?: string }): Promise<Rulebook> =>
    (await api.put("/proposal-core/rulebooks/" + id, data)).data,
  activateRulebook: async (id: number): Promise<Rulebook> => (await api.patch("/proposal-core/rulebooks/" + id + "/activate")).data,
  deleteRulebook: async (id: number) => (await api.delete("/proposal-core/rulebooks/" + id)).data,
  listReferences: async (): Promise<ReferenceAccount[]> => (await api.get("/proposal-core/references")).data,
  createReference: async (data: Partial<ReferenceAccount>): Promise<ReferenceAccount> => (await api.post("/proposal-core/references", data)).data,
  updateReference: async (id: number, data: Partial<ReferenceAccount>): Promise<ReferenceAccount> => (await api.put("/proposal-core/references/" + id, data)).data,
  deleteReference: async (id: number) => (await api.delete("/proposal-core/references/" + id)).data,
  listTerms: async (): Promise<ForbiddenTerm[]> => (await api.get("/proposal-core/terms")).data,
  createTerm: async (data: Partial<ForbiddenTerm>): Promise<ForbiddenTerm> => (await api.post("/proposal-core/terms", data)).data,
  updateTerm: async (id: number, data: Partial<ForbiddenTerm>): Promise<ForbiddenTerm> => (await api.put("/proposal-core/terms/" + id, data)).data,
  deleteTerm: async (id: number) => (await api.delete("/proposal-core/terms/" + id)).data,
  checkText: async (text: string, clientName?: string): Promise<{ blocked: boolean; hits: TextCheckHit[] }> =>
    (await api.post("/proposal-core/check-text", { text, clientName })).data,
};

export interface GenerationReadiness { ok: boolean; motivos: string[]; briefingPendencias: number; rulebookVersion: string | null; chrome: boolean; ia: boolean }
export interface GenerationJob { status: "running" | "done" | "error" | "not_found"; step?: string; documentId?: number; error?: string }
export interface ProposalDocument {
  id: number; proposalId: number; version: number; htmlS3Key: string | null; pdfS3Key: string | null;
  gateStatus: "aprovado" | "aviso" | "bloqueado" | "erro";
  gateReport: { falhas: string[]; avisos: string[]; paginas?: number };
  rulebookVersion: string | null; model: string | null; costUsd: number | null; createdAt: string;
}
/** Geração da proposta comercial pela IA (fase 4): prontidão, job, versões, gate e publicação. */
export const proposalGenerationService = {
  readiness: async (proposalId: number): Promise<GenerationReadiness> => (await api.get("/proposal-generation/" + proposalId + "/readiness")).data,
  start: async (proposalId: number): Promise<{ jobId: string }> => (await api.post("/proposal-generation/" + proposalId + "/start")).data,
  job: async (jobId: string): Promise<GenerationJob> => (await api.get("/proposal-generation/jobs/" + jobId)).data,
  documents: async (proposalId: number): Promise<ProposalDocument[]> => (await api.get("/proposal-generation/" + proposalId + "/documents")).data,
  urls: async (docId: number): Promise<{ htmlUrl: string | null; pdfUrl: string | null }> => (await api.get("/proposal-generation/documents/" + docId + "/urls")).data,
  approve: async (docId: number) => (await api.post("/proposal-generation/documents/" + docId + "/approve")).data,
};

/**
 * Gravar o rateio do grupo (`POST /clients/:id/rateio`). Quem chama: ListasDoRateio (trocar as
 * listas), RedividirRateio (mesmas listas, base de hoje) e ComecarADividir (a primeira versão, que
 * o backend cria com o motor desligado).
 *
 * Se a cota acordada de alguma empresa diverge da calculada, o backend responde 409
 * (`cotas_acordadas_divergem`; ler com `lerCotasDivergentes` de
 * utils/cotasDivergentes). Para gravar mesmo assim, reenviar o MESMO corpo com
 * `recalcularCotas: true`; nunca mandar o campo por padrão.
 */
export const rateioService = {
  salvar: async (clientId: number, corpo: Record<string, unknown>, opcoes?: { recalcularCotas?: boolean }) =>
    (await api.post("/clients/" + clientId + "/rateio", opcoes?.recalcularCotas ? { ...corpo, recalcularCotas: true } : corpo)).data,
  // Redividir (ADMIN): simular não grava nada; devolve a divisão pela base de HOJE e o
  // `poolParaConfirmar`, que o salvar exige (se a base mudar entre os dois, 400).
  simularDivisao: async (clientId: number, corpo: { criterio?: string; produtoIds?: number[]; produtoIdsBase?: number[]; descontosSeguemRateio?: boolean }): Promise<SimulacaoDaDivisao> =>
    (await api.post("/clients/" + clientId + "/rateio/simular", corpo)).data,
  // Motor do rateio (ADMIN). A tela LÊ o estado ao abrir; simular é ato do operador,
  // porque carimba a prova (dryRunEm). Ligar e desligar: sempre pela rota, nunca UPDATE.
  estadoDoMotor: async (clientId: number): Promise<EstadoDoMotor> =>
    (await api.get("/clients/" + clientId + "/rateio/motor")).data,
  simularMotor: async (clientId: number, competencia?: string): Promise<ResumoDoDryRun & { motorAtivo: boolean; salvo: boolean }> =>
    (await api.post("/clients/" + clientId + "/rateio/motor/dry-run", competencia ? { competencia, salvar: true } : { salvar: true })).data,
  ligarMotor: async (clientId: number) =>
    (await api.post("/clients/" + clientId + "/rateio/motor", { ligar: true })).data,
  desligarMotor: async (clientId: number) =>
    (await api.post("/clients/" + clientId + "/rateio/motor", { ligar: false })).data,
  declararCompetenciaPassada: async (clientId: number, corpo: DeclaracaoDeCompetenciaPassada) =>
    (await api.post("/clients/" + clientId + "/rateio/competencia-passada", corpo)).data,
};

/** Guia de Preços: o backend calcula faixa, piso e memória de cálculo. O front nunca calcula. */
export const pricingService = {
  quote: async (
    lines: Array<{ productId: number; quantity: number }>,
  ): Promise<Quote> => (await api.post("/pricing/quote", { lines })).data,
};

export const clientsService = {
  getAll: async () => (await api.get("/clients")).data,
  /** O cadastro ATUAL do cliente, com phones e emails (GET /clients/:id). O modal nasce desta leitura. */
  getById: async (id: number) => (await api.get("/clients/" + id)).data,
  // Rateio do grupo do cliente (qualquer membro). Só leitura. `competencia`
  // 'YYYY-MM' escolhe a versão histórica; sem ela, a vigente.
  getRateio: async (id: number, competencia?: string): Promise<RateioDoGrupo> =>
    (await api.get("/clients/" + id + "/rateio", { params: competencia ? { competencia } : undefined })).data,
  getWithoutFinancialEmail: async () =>
    (await api.get("/clients/without-financial-email")).data,
  create: async (data: any) => (await api.post("/clients", data)).data,
  update: async (id: number, data: any) =>
    (await api.put("/clients/" + id, data)).data,
  /**
   * Endereços de acesso do cliente (E1, backend #514). SUBSTITUI o conjunto: manda os ids que devem ficar. Desvincular
   * um endereço que DDRs do cliente ainda usam volta 409 ENDERECO_EM_USO, e nada é gravado. Rota própria, fora do
   * Salvar do modal (como a retenção S3): vincular é efeito imediato, não depende de salvar a ficha.
   */
  definirEnderecosDeAcesso: async (id: number, ids: number[]): Promise<EnderecoDeAcesso[]> =>
    (await api.put("/clients/" + id + "/access-addresses", { ids })).data,
  /**
   * Regra de cobrança por e-mail do cliente. Endpoint próprio, fora do update:
   * é chamado do modal de cobrança, que não carregou o cadastro inteiro — e o
   * `whitelist` do backend apagaria em silêncio o que faltasse num PUT completo.
   */
  setDunningMode: async (
    id: number,
    mode: "auto" | "manual" | "never" | null,
    reason?: string,
  ) => (await api.put("/clients/" + id + "/dunning-mode", { mode, reason })).data,
  delete: async (id: number) => (await api.delete("/clients/" + id)).data,
  updateAllFromReceita: async () =>
    (await api.post("/clients/update-all-from-receita")).data,
  importFinancialEmails: async (
    emails: Array<{ cnpj: string; emails: string[] }>,
  ) => (await api.post("/clients/import-financial-emails", { emails })).data,

  // --- Retenção de áudios S3 ---
  listEc2Instances: async (): Promise<Ec2Instance[]> =>
    (await api.get('/clients/ec2/instances')).data,
  listS3Buckets: async (): Promise<string[]> =>
    (await api.get("/clients/s3/buckets")).data,
  getS3Retention: async (id: number): Promise<S3RetentionStatus> =>
    (await api.get("/clients/" + id + "/s3-retention")).data,
  listS3Prefixes: async (id: number): Promise<string[]> =>
    (await api.get("/clients/" + id + "/s3-retention/prefixes")).data,
  saveS3Retention: async (
    id: number,
    enabled: boolean,
    rules: S3RetentionRule[],
    bucket?: string,
    naoSeAplica?: { notApplicable: boolean; notApplicableReason?: string },
  ) =>
    (
      await api.put("/clients/" + id + "/s3-retention", {
        enabled,
        bucket,
        rules,
        ...naoSeAplica,
      })
    ).data,
  simulateS3Retention: async (
    id: number,
    rule: S3RetentionRule,
    bucket?: string,
  ): Promise<S3RetentionSimulation> =>
    (
      await api.post("/clients/" + id + "/s3-retention/simulate", {
        ...rule,
        bucket,
      })
    ).data,
};

/** Campos de retenção que a lista de clientes usa (vêm no GET /clients). */
export interface ClientS3Fields {
  s3RetentionEnabled?: boolean;
  /** Cacheado: o bucket tem pasta de gravação? null = nunca verificado. */
  s3HasAudio?: boolean | null;
  /** Marcado à mão: não há gravação em nuvem para reter. */
  s3RetentionNotApplicable?: boolean;
  s3RetentionNotApplicableReason?: string | null;
  s3RetentionRules?: Array<{ prefix: string; retentionDays: number }>;
}

export interface Ec2Instance {
  id: string;
  nome: string | null;
  ip: string | null;
  estado: string;
  regiao: string;
}

export interface S3RetentionRule {
  prefix: string;
  retentionDays: number;
  glacierDays?: number;
}

export interface S3RetentionStatus {
  enabled: boolean;
  /** "Não se aplica": cliente sem gravação em nuvem para reter. */
  notApplicable: boolean;
  notApplicableReason: string | null;
  bucket: string | null;
  /** true SÓ quando a AWS confirmou o bucket (com a #405; antes, qualquer erro virava false). */
  bucketExists: boolean;
  /** #405 do backend: o que a AWS respondeu. Ausente em backend sem a #405 (ver utils/s3Bucket). */
  bucketCheck?: 'confirmado' | 'inexistente' | 'nao_confirmado';
  /** Frase pronta para a tela; null quando confirmado. */
  bucketCheckMotivo?: string | null;
  /** Código cru da AWS (AccessDenied, ExpiredToken…). */
  bucketCheckErro?: string | null;
  rules: Array<{ prefix: string; retentionDays: number; glacierDays: number }>;
  appliedRuleIds: string[];
  /** O que está VALENDO na AWS agora, não o que está no cadastro. */
  appliedRules: Array<{
    prefix: string;
    retentionDays: number | null;
    glacierDays: number | null;
  }>;
  /** Pastas de áudio presentes no bucket que nenhuma regra cobre (ex: storage1/). */
  uncoveredPrefixes: string[];
}

export interface S3RetentionSimulation {
  /** true = números vieram de amostra por pasta, não de varredura completa. */
  estimated: boolean;
  sampledFolders: number;
  totalFolders: number;
  expiring: {
    objects: number;
    bytes: number;
    folders: number;
    from: string | null;
    to: string | null;
  };
  transitioning: { objects: number; bytes: number; folders: number };
  stayingStandard: { objects: number; bytes: number; folders: number };
  smallObjects: number;
  oldestKeyDate: string | null;
  /** Pasta que não é particionada por dia: não dá para classificar barato. */
  unsupportedLayout: boolean;
}

export const telecomCarriersService = {
  getAll: async () => (await api.get("/telecom-carriers")).data,
  create: async (data: {
    nome: string;
    razaoSocial?: string;
    cnpj?: string;
    ativo?: boolean;
    notes?: string;
  }) => (await api.post("/telecom-carriers", data)).data,
  update: async (
    id: number,
    data: {
      nome?: string;
      razaoSocial?: string;
      cnpj?: string;
      ativo?: boolean;
      notes?: string;
    },
  ) => (await api.put("/telecom-carriers/" + id, data)).data,
  delete: async (id: number) =>
    (await api.delete("/telecom-carriers/" + id)).data,
};

/**
 * As NOTAS DE EMPENHO da assinatura (fase 2 do plano de empenho, backend #468). O saldo é derivado no backend (valor e
 * competências) a partir dos consumos, um por fatura aprovada. Ver utils/empenho e components/empenho.
 * ⚠️ O PUT mescla com `??`: `null` MANTÉM o valor antigo. Para limpar o escopo, mande `[]`; a observação, `''`.
 */
export const contractEmpenhosService = {
  listar: async (contractId: number) => (await api.get("/contracts/" + contractId + "/empenhos")).data,
  /** Prévia (não grava) do que entra ao cadastrar: as faturas já aprovadas da faixa e o saldo que resulta. */
  previa: async (contractId: number, dados: any) => (await api.post("/contracts/" + contractId + "/empenhos/preview", dados)).data,
  criar: async (contractId: number, dados: any) => (await api.post("/contracts/" + contractId + "/empenhos", dados)).data,
  atualizar: async (empenhoId: number, dados: any) => (await api.put("/contracts/empenhos/" + empenhoId, dados)).data,
  excluir: async (empenhoId: number) => (await api.delete("/contracts/empenhos/" + empenhoId)).data,
  anexar: async (empenhoId: number, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return (await api.post("/contracts/empenhos/" + empenhoId + "/anexo", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
  },
  urlDoAnexo: async (empenhoId: number) => (await api.get("/contracts/empenhos/" + empenhoId + "/anexo/url")).data,
  /** Fase 3 (backend #494): LÊ o PDF da nota e devolve os campos com confiança. Não grava nada. Ver utils/empenhoDoPdf. */
  lerPdf: async (contractId: number, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return (await api.post("/contracts/" + contractId + "/empenhos/ler-pdf", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
  },
};

export const contractsService = {
  getAll: async () => (await api.get("/contracts")).data,
  /** Todos os contratos do cliente, de qualquer status (GET /contracts/client/:id). */
  getByClient: async (clientId: number) => (await api.get("/contracts/client/" + clientId)).data,
  /** A assinatura ATUAL, para o modal reler ao abrir (GET /contracts/:id). */
  getById: async (id: number) => (await api.get("/contracts/" + id)).data,
  create: async (data: any) => {
    // SERIALIZAR items como JSON string (mesma solução usada em proposals)
    const payload = { ...data, itemsJson: JSON.stringify(data.items || []) };
    delete payload.items;
    return (await api.post("/contracts", payload)).data;
  },
  update: async (id: number, data: any) => {
    // SERIALIZAR items como JSON string (mesma solução usada em proposals)
    const payload = { ...data, itemsJson: JSON.stringify(data.items || []) };
    delete payload.items;
    return (await api.put("/contracts/" + id, payload)).data;
  },
  delete: async (id: number) => (await api.delete("/contracts/" + id)).data,
  syncSignedPdf: async (id: number) =>
    (await api.post("/contracts/" + id + "/sync-signed-pdf")).data,
  syncProposalPdf: async (id: number) =>
    (await api.post("/contracts/" + id + "/sync-proposal-pdf")).data,
  // ClickSign integration
  sendForSignature: async (
    id: number,
    data: { signerEmail: string; signerName: string; signerCpf?: string },
  ) => (await api.post("/contracts/" + id + "/send-for-signature", data)).data,
  getPdf: async (id: number) =>
    (await api.get("/contracts/" + id + "/pdf", { timeout: PRAZO_DE_ARQUIVO_MS, responseType: "blob" })).data,
  getPdfBase64: async (id: number) =>
    (await api.get("/contracts/" + id + "/pdf-base64", { timeout: PRAZO_DE_ARQUIVO_MS })).data,
  resendSignatureNotification: async (id: number) =>
    (await api.post("/contracts/" + id + "/resend-signature-notification"))
      .data,
  cancelSignature: async (id: number) =>
    (await api.post("/contracts/" + id + "/cancel-signature")).data,
  // Attachments
  addAttachment: async (
    id: number,
    data: {
      filename: string;
      fileBase64: string;
      description?: string;
      uploadedBy?: string;
      category?: string;
    },
  ) => (await api.post("/contracts/" + id + "/attachments", data)).data,
  removeAttachment: async (id: number, attachmentId: string) =>
    (await api.delete("/contracts/" + id + "/attachments/" + attachmentId))
      .data,
  updateAttachmentCategory: async (
    id: number,
    attachmentId: string,
    category: string,
  ) =>
    (
      await api.patch(
        "/contracts/" + id + "/attachments/" + attachmentId + "/category",
        { category },
      )
    ).data,
  getAttachmentDownloadUrl: (id: number, attachmentId: string) =>
    `${api.defaults.baseURL}/contracts/${id}/attachments/${attachmentId}/download`,
  downloadAttachment: async (
    id: number,
    attachmentId: string,
    filename: string,
  ) => {
    const response = await api.get(
      `/contracts/${id}/attachments/${attachmentId}/download`,
      {
        responseType: "blob",
      },
    );
    const blob = new Blob([response.data], {
      type: response.headers["content-type"],
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
  viewAttachment: async (id: number, attachmentId: string) => {
    const response = await api.get(
      `/contracts/${id}/attachments/${attachmentId}/download`,
      {
        responseType: "blob",
      },
    );
    const blob = new Blob([response.data], {
      type: response.headers["content-type"],
    });
    const url = window.URL.createObjectURL(blob);
    window.open(url, "_blank");
  },
  // Suspension
  suspendContract: async (
    id: number,
    data: { reason: string; reactivationDate?: string },
  ) => (await api.post("/contracts/" + id + "/suspend", data)).data,
  reactivateContract: async (id: number) =>
    (await api.post("/contracts/" + id + "/reactivate")).data,
  // Admin
  syncProductDescriptions: async () =>
    (await api.post("/contracts/admin/sync-product-descriptions")).data,
  // P&S Items
  getPendingPSItems: async (id: number) =>
    (await api.get("/contracts/" + id + "/pending-ps-items")).data,
  markPSItemBilled: async (id: number, itemIndex: number) =>
    (await api.post("/contracts/" + id + "/mark-ps-item-billed", { itemIndex }))
      .data,
};

// Anualidades de reajuste (contract_adjustments). O contrato guarda a regra;
// cada anualidade guarda o fato. Cálculo de índice/retroativo é do backend.
export const contractAdjustmentsService = {
  getAll: async (filters?: { contractId?: number; clientId?: number; status?: string; pending?: boolean }) => {
    const params = new URLSearchParams();
    if (filters?.contractId) params.append("contractId", String(filters.contractId));
    if (filters?.clientId) params.append("clientId", String(filters.clientId));
    if (filters?.status) params.append("status", filters.status);
    if (filters?.pending) params.append("pending", "true");
    const query = params.toString();
    return (await api.get("/contract-adjustments" + (query ? "?" + query : ""))).data;
  },
  getByContract: async (contractId: number) =>
    (await api.get("/contract-adjustments/contract/" + contractId)).data,
  getById: async (id: number) => (await api.get("/contract-adjustments/" + id)).data,
  create: async (data: any) => (await api.post("/contract-adjustments", data)).data,
  update: async (id: number, data: any) =>
    (await api.put("/contract-adjustments/" + id, data)).data,
  delete: async (id: number) => (await api.delete("/contract-adjustments/" + id)).data,
  // Gera as anualidades vencidas desde a data-base; dryRun só calcula e devolve.
  generate: async (contractId: number, dryRun = false) =>
    (await api.post("/contract-adjustments/generate/" + contractId + (dryRun ? "?dryRun=true" : ""))).data,
  generateAll: async () => (await api.post("/contract-adjustments/generate-all")).data,
  // Rascunho do ofício de reajuste da anualidade, já vinculado (adjustmentId).
  createOficio: async (id: number) => (await api.post("/contract-adjustments/" + id + "/oficio")).data,
  // Registrar apostila/aditivo: deferida → contrato reajustado → complementar → aplicada. dryRun = prévia.
  registerApostila: async (id: number, data: any) => (await api.post("/contract-adjustments/" + id + "/apostila", data)).data,
  /**
   * Termos aditivos ASSINADOS que ainda não viraram vigência nem anualidade
   * (backend #238). Lista própria, não grupo da fila de anualidades: estes
   * documentos existem justamente porque a anualidade ainda não existe.
   */
  aditivosPendentes: async () => (await api.get("/contract-adjustments/aditivos-pendentes")).data,
  /**
   * Aplica o aditivo: vigência, regra de reajuste e a anualidade que ele
   * instrumentaliza. `dryRun: true` devolve o MESMO objeto de prévia do
   * apostilamento sem gravar nada — passe por ela sempre: aplicar mexe em MRR,
   * em item, em tarifa de consumo e pode emitir fatura complementar.
   */
  aplicarAditivo: async (
    digitalContractId: number,
    data: { effectsFrom: string; applyAt?: string | null; percentual?: number | null; aniversario?: string | null; attachmentId?: string | null; dryRun?: boolean },
  ) => (await api.post("/contract-adjustments/aditivos/" + digitalContractId + "/aplicar", data)).data,
  // Linha do tempo da anualidade. O GET devolve o registrado a mao MAIS o
  // derivado (oficio emitido/assinado/revogado, complementar), este ultimo com
  // id null e derived true, montado na leitura e sem linha no banco.
  listEvents: async (id: number) => (await api.get("/contract-adjustments/" + id + "/events")).data,
  addEvent: async (id: number, data: any) => (await api.post("/contract-adjustments/" + id + "/events", data)).data,
  removeEvent: async (id: number, eventId: number) =>
    (await api.delete("/contract-adjustments/" + id + "/events/" + eventId)).data,
  // Dossie do CONTRATO numa chamada so: a regra com a fonte onde foi lida, a
  // escada de valores (a cadeia inteira, para ver se um degrau partiu da base
  // errada) e a linha do tempo unificada de todas as anualidades, porque a
  // conversa com o orgao nao respeita a fronteira do ciclo anual.
  // A fila de trabalho: pendentes agrupadas por VERBO, saindo do mesmo
  // alarmBuckets que monta o e-mail das 07h. Tela e alarme nao podem divergir.
  getFila: async () => (await api.get("/contract-adjustments/fila")).data,
  getDossie: async (contractId: number) =>
    (await api.get("/contract-adjustments/contract/" + contractId + "/dossie")).data,
  // Evento preso ao CONTRATO, sem anualidade: e o unico jeito de registrar a
  // conversa que comeca antes de existir ciclo anual (pedir a data-base, p.ex.).
  addContractEvent: async (contractId: number, data: any) =>
    (await api.post("/contract-adjustments/contract/" + contractId + "/events", data)).data,
  // Rascunho do proximo e-mail. Corpo vazio apaga.
  salvarRascunho: async (contractId: number, data: any) =>
    (await api.put("/contract-adjustments/contract/" + contractId + "/rascunho", data)).data,
  // Marcou como enviado: o rascunho vira evento datado e sai do contrato.
  rascunhoEnviado: async (contractId: number, data: any = {}) =>
    (await api.post("/contract-adjustments/contract/" + contractId + "/rascunho/enviado", data)).data,
  // Resumo por CLIENTE: quem esta em dia, quem tem anualidade viva, quem venceu
  // sem anualidade gerada e quem nunca teve a regra lida no documento.
  getResumo: async () => (await api.get("/contract-adjustments/resumo")).data,
  getIndexValues: async (indexName?: string) =>
    (await api.get("/contract-adjustments/index-values" + (indexName ? "?indexName=" + indexName : ""))).data,
  refreshIndexValues: async () => (await api.post("/contract-adjustments/index-values/refresh")).data,
};

export const usersService = {
  getAll: async () => (await api.get("/users")).data,
  getById: async (id: number) => (await api.get("/users/" + id)).data,
  create: async (data: any) => (await api.post("/users", data)).data,
  update: async (id: number, data: any) =>
    (await api.put("/users/" + id, data)).data,
  delete: async (id: number) => (await api.delete("/users/" + id)).data,
};

export const permissionsService = {
  getAll: async () => (await api.get("/permissions")).data,
  getGrouped: async () => (await api.get("/permissions/grouped")).data,
  getByUser: async (userId: number) =>
    (await api.get("/permissions/user/" + userId)).data,
  getDefaultsForRole: async (role: string) =>
    (await api.get("/permissions/defaults/" + role)).data,
  assignToUser: async (userId: number, permissions: string[]) =>
    (await api.post("/permissions/user/" + userId + "/assign", { permissions }))
      .data,
  addToUser: async (userId: number, permissions: string[]) =>
    (await api.post("/permissions/user/" + userId + "/add", { permissions }))
      .data,
  removeFromUser: async (userId: number, permissions: string[]) =>
    (await api.post("/permissions/user/" + userId + "/remove", { permissions }))
      .data,
  applyDefaults: async (userId: number) =>
    (await api.post("/permissions/user/" + userId + "/apply-defaults")).data,
  applyDefaultsToRole: async (role: string) =>
    (await api.post("/permissions/role/" + role + "/apply-defaults-all")).data,
  seed: async () => (await api.post("/permissions/seed")).data,
};

export const leadsService = {
  getAll: async () => (await api.get("/leads")).data,
  getById: async (id: number) => (await api.get("/leads/" + id)).data,
  create: async (data: any) => (await api.post("/leads", data)).data,
  /** A4 (#409 do backend): as etapas do funil. 404 em backend sem a #409 (o front cai nas seis fixas). */
  getFunnelStages: async () => (await api.get("/leads/funnel-stages")).data,
  putFunnelStages: async (etapas: any[]) => (await api.put("/leads/funnel-stages", { etapas })).data,
  /** B1 (#411 do backend): concluir a próxima ação carimba a data e limpa texto e prazo, numa operação só. */
  concluirProximaAcao: async (id: number) => (await api.patch(`/leads/${id}/proxima-acao/concluir`)).data,
  update: async (id: number, data: any) =>
    (await api.put("/leads/" + id, data)).data,
  updateStatus: async (id: number, status: string, lostReason?: string) =>
    (await api.patch("/leads/" + id + "/status", { status, lostReason })).data,
  freeze: async (id: number, data: { frozenUntil?: string; reason?: string }) =>
    (await api.patch("/leads/" + id + "/freeze", data)).data,
  unfreeze: async (id: number) =>
    (await api.patch("/leads/" + id + "/unfreeze")).data,
  delete: async (id: number) => (await api.delete("/leads/" + id)).data,
};

export const proposalsService = {
  getAll: async () => (await api.get("/proposals")).data,
  getById: async (id: number) => (await api.get("/proposals/" + id)).data,
  getByCode: async (code: string) =>
    (await api.get("/proposals/code/" + code)).data,
  getByLead: async (leadId: number) =>
    (await api.get("/proposals/lead/" + leadId)).data,
  create: async (data: any) => {
    // SERIALIZAR items como JSON string
    const payload = { ...data, itemsJson: JSON.stringify(data.items || []) };
    delete payload.items;
    return (await api.post("/proposals", payload)).data;
  },
  update: async (id: number, data: any) => {
    // SERIALIZAR items como JSON string
    const payload = { ...data, itemsJson: JSON.stringify(data.items || []) };
    delete payload.items;
    console.log("ENVIANDO itemsJson:", payload.itemsJson);
    return (await api.patch("/proposals/" + id, payload)).data;
  },
  /** E7 (12/10/2026): o aceite pelo operador leva quem aceitou e o canal; o backend grava em acceptedBy*. */
  updateStatus: async (id: number, status: string, aceite?: { acceptedByName: string; acceptanceChannel: string }) =>
    (await api.patch("/proposals/" + id + "/status", { status, ...(aceite || {}) })).data,
  duplicate: async (id: number) =>
    (await api.post("/proposals/" + id + "/duplicate")).data,
  delete: async (id: number) => (await api.delete("/proposals/" + id)).data,
  sendEmail: async (id: number, emails: string[]) =>
    (await api.post("/proposals/" + id + "/send-email", { emails })).data,
  uploadCustomPdf: async (id: number, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return (
      await api.post("/proposals/" + id + "/custom-pdf", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    ).data;
  },
  removeCustomPdf: async (id: number) =>
    (await api.delete("/proposals/" + id + "/custom-pdf")).data,
  // Analisa o HTML da proposta com IA e devolve itens HUB (código+qtd) p/ pré-preencher o import
  analyzeDocument: async (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return (
      await api.post("/proposals/analyze-document", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      })
    ).data;
  },
  // Assinatura do PDF timbrado (ClickSign, mesmo padrão dos ofícios)
  sendForSignature: async (
    id: number,
    data: { signers: any[]; templateId?: number },
  ) => (await api.post("/proposals/" + id + "/send-for-signature", data)).data,
  getSignatureStatus: async (id: number) =>
    (await api.get("/proposals/" + id + "/signature-status")).data,
  resendSignatureNotification: async (id: number) =>
    (await api.post("/proposals/" + id + "/resend-signature-notification"))
      .data,
  cancelSignature: async (id: number) =>
    (await api.post("/proposals/" + id + "/cancel-signature")).data,
  getSignedPdf: async (id: number) =>
    (
      await api.get("/proposals/" + id + "/signed-pdf", { timeout: PRAZO_DE_ARQUIVO_MS,
        responseType: "blob",
      })
    ).data,
};

export const cnpjService = {
  search: async (cnpj: string) => {
    const cleanCnpj = cnpj.replace(/\D/g, "");
    if (cleanCnpj.length !== 14) return null;
    try {
      return (await api.get("/cnpj/" + cleanCnpj)).data;
    } catch {
      return null;
    }
  },
};

export const templatesService = {
  getAll: async () => (await api.get("/templates")).data,
  getActive: async () => (await api.get("/templates/active")).data,
  getDefault: async () => (await api.get("/templates/default")).data,
  getById: async (id: number) => (await api.get("/templates/" + id)).data,
  create: async (data: any) => (await api.post("/templates", data)).data,
  update: async (id: number, data: any) =>
    (await api.patch("/templates/" + id, data)).data,
  delete: async (id: number) => (await api.delete("/templates/" + id)).data,
  uploadPage: async (
    id: number,
    file: File,
    name: string,
    type: string,
    order: number,
  ) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", name);
    formData.append("type", type);
    formData.append("order", order.toString());
    return (
      await api.post("/templates/" + id + "/upload-page", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    ).data;
  },
};

export const digitalContractsService = {
  getAll: async () => (await api.get("/digital-contracts")).data,
  getById: async (id: number) =>
    (await api.get("/digital-contracts/" + id)).data,
  getByLead: async (leadId: number) =>
    (await api.get("/digital-contracts/lead/" + leadId)).data,
  getByProposal: async (proposalId: number) =>
    (await api.get("/digital-contracts/proposal/" + proposalId)).data,
  /**
   * Os termos aditivos de um contrato (backend #236). Existe porque aditivo não
   * tem lead nem proposta: o Sicoob é cliente desde 2021, e até esta rota o
   * aditivo #54 estava gravado e não aparecia em lugar nenhum da interface.
   */
  getByContract: async (contractId: number) =>
    (await api.get("/digital-contracts/contract/" + contractId)).data,
  create: async (data: any) =>
    (await api.post("/digital-contracts", data)).data,
  update: async (id: number, data: any) =>
    (await api.put("/digital-contracts/" + id, data)).data,
  updateStatus: async (id: number, status: string) =>
    (await api.put("/digital-contracts/" + id + "/status", { status })).data,
  markAsSigned: async (id: number, signedPdfUrl?: string) =>
    (await api.put("/digital-contracts/" + id + "/sign", { signedPdfUrl }))
      .data,
  delete: async (id: number) =>
    (await api.delete("/digital-contracts/" + id)).data,
  // ClickSign integration
  sendForSignature: async (
    id: number,
    data: {
      signerEmail?: string;
      signerName?: string;
      signerCpf?: string;
      signers?: {
        name: string;
        email: string;
        cpf?: string;
        role?: "sign" | "witness";
      }[];
    },
  ) =>
    (await api.post("/digital-contracts/" + id + "/send-for-signature", data))
      .data,
  // Envia a minuta (PDF anexo) pros destinatários VALIDAREM antes da assinatura — não toca ClickSign.
  sendForReview: async (
    id: number,
    emails?: string[],
  ): Promise<{
    success: boolean;
    sentTo: string[];
    errors: string[];
    message: string;
  }> =>
    (
      await api.post(
        "/digital-contracts/" + id + "/send-for-review",
        emails?.length ? { emails } : {},
      )
    ).data,
  getPdf: async (id: number) =>
    (
      await api.get("/digital-contracts/" + id + "/pdf", { timeout: PRAZO_DE_ARQUIVO_MS,
        responseType: "blob",
      })
    ).data,
  // Retorna { url } da ClickSign (autenticado). Frontend abre a URL direto —
  // não dá pra seguir o redirect 302 via XHR pois o S3 da ClickSign não envia CORS.
  getSignedUrl: async (id: number): Promise<{ url: string }> =>
    (
      await api.get("/digital-contracts/" + id + "/signed-pdf", {
        params: { format: "json" },
      })
    ).data,
  resendSignatureNotification: async (id: number) =>
    (
      await api.post(
        "/digital-contracts/" + id + "/resend-signature-notification",
      )
    ).data,
  cancelSignature: async (id: number) =>
    (await api.post("/digital-contracts/" + id + "/cancel-signature")).data,
  getSignersStatus: async (id: number) =>
    (await api.get("/digital-contracts/" + id + "/signers-status")).data,
};

export const activityLogService = {
  getAll: async (limit: number = 100) =>
    (await api.get("/activity-logs", { params: { limit } })).data,
  getByUser: async (userId: number, limit: number = 50) =>
    (await api.get("/activity-logs/user", { params: { userId, limit } })).data,
  getByEntity: async (entityType: string, entityId: number) =>
    (
      await api.get("/activity-logs/entity", {
        params: { entityType, entityId },
      })
    ).data,
  create: async (data: {
    actionText: string;
    details: string;
    entityType?: string;
    entityId?: number;
  }) => (await api.post("/activity-logs", data)).data,
  // `clear` (DELETE /activity-logs/clear, apagava o histórico inteiro) saiu em 12/10/2026: o backend #414 removeu a rota,
  // e a tela já não a chamava (App.administracao.test assere isso). O que existe é o cleanup, com data de corte.
  // A prévia (só leitura) conta o que sairia; o DELETE recusa (409) sem `confirmar` = o número que ela mostrou, e reconta.
  cleanupPreview: async (days: number) =>
    (await api.get("/activity-logs/cleanup/preview", { params: { days } })).data,
  cleanup: async (days: number, confirmar: number) =>
    (await api.delete("/activity-logs/cleanup", { params: { days, confirmar } })).data,
};

export const clientLocationsService = {
  getAll: async () => (await api.get("/client-locations")).data,
  getByClient: async (clientId: number) =>
    (await api.get("/client-locations/client/" + clientId)).data,
  getById: async (id: number) =>
    (await api.get("/client-locations/" + id)).data,
  create: async (data: any) => (await api.post("/client-locations", data)).data,
  update: async (id: number, data: any) =>
    (await api.put("/client-locations/" + id, data)).data,
  delete: async (id: number) =>
    (await api.delete("/client-locations/" + id)).data,
};

export const clientPhonesService = {
  getAll: async () => (await api.get("/client-phones")).data,
  getByClient: async (clientId: number) =>
    (await api.get("/client-phones/client/" + clientId)).data,
  create: async (data: any) => (await api.post("/client-phones", data)).data,
  update: async (id: number, data: any) =>
    (await api.put("/client-phones/" + id, data)).data,
  delete: async (id: number) => (await api.delete("/client-phones/" + id)).data,
};

export const clientEmailsService = {
  getAll: async () => (await api.get("/client-emails")).data,
  getByClient: async (clientId: number) =>
    (await api.get("/client-emails/client/" + clientId)).data,
  create: async (data: any) => (await api.post("/client-emails", data)).data,
  update: async (id: number, data: any) =>
    (await api.put("/client-emails/" + id, data)).data,
  delete: async (id: number) => (await api.delete("/client-emails/" + id)).data,
};

export const clientContactsService = {
  getAll: async () => (await api.get("/client-contacts")).data,
  getByClient: async (clientId: number) =>
    (await api.get("/client-contacts/client/" + clientId)).data,
  create: async (data: any) => (await api.post("/client-contacts", data)).data,
  update: async (id: number, data: any) =>
    (await api.put("/client-contacts/" + id, data)).data,
  delete: async (id: number) =>
    (await api.delete("/client-contacts/" + id)).data,
};

/**
 * O Dashboard (módulo `dashboard` do backend, PR #524 de 23/10/2026). A série mensal do gráfico de evolução: faturado por
 * competência (sem Rascunho/Cancelada) e recebido pelo mês do pagamento (invoice_payments). Nunca antes de 2026-02 (jan foi
 * a transição do Superlógica), até 12 meses, o mês corrente com `parcial: true`. `mrr` é a foto do mês, null sem foto.
 */
export interface MesDaEvolucao {
  mes: string;
  faturado: number;
  faturas: number;
  recebido: number;
  pagamentos: number;
  mrr: number | null;
  parcial: boolean;
}

/**
 * Os recebimentos de um mês (PR #525 do backend, 23/10/2026): a base são os PAGAMENTOS (invoice_payments), não o valor
 * pago das faturas. `faturas` é a contagem distinta de faturas tocadas. O pagamento sem forma herda a da fatura, e o
 * backend diz quando (`herdadas`, `formaHerdada`). `forma: null` é "sem forma" (ninguém sabe).
 */
export interface PastilhaDeForma {
  forma: string | null;
  valor: number;
  pagamentos: number;
  herdadas: number;
}
export interface RecebimentoDoMes {
  invoiceId: number | null;
  code: string | null;
  cliente: string | null;
  valor: number;
  data: string;
  forma: string | null;
  formaHerdada: boolean;
}
export interface RecebimentosDoMes {
  mes: string;
  total: number;
  pagamentos: number;
  faturas: number;
  porForma: PastilhaDeForma[];
  ultimos: RecebimentoDoMes[];
}

export const dashboardService = {
  evolucao: async (): Promise<{ meses: MesDaEvolucao[] }> => (await api.get("/dashboard/evolucao")).data,
  // Sem `mes`, o backend usa o mês corrente.
  recebimentos: async (mes?: string): Promise<RecebimentosDoMes> =>
    (await api.get("/dashboard/recebimentos", { params: mes ? { mes } : undefined })).data,
};

export const invoicesService = {
  // `status` aceita lista ("Aberta,Parcial"). Sem ele, devolve tudo (retrocompatível).
  getAll: async (params?: { status?: string }) =>
    (
      await api.get(
        "/invoices" +
          (params?.status
            ? `?status=${encodeURIComponent(params.status)}`
            : ""),
      )
    ).data,
  getById: async (id: number) => (await api.get("/invoices/" + id)).data,
  getPayments: async (id: number) =>
    (await api.get("/invoices/" + id + "/payments")).data,
  getFiscalDocumentsPaymentStatus: async (id: number) =>
    (await api.get("/invoices/" + id + "/fiscal-documents-payment-status"))
      .data,
  getByCode: async (code: string) =>
    (await api.get("/invoices/code/" + code)).data,
  getByClient: async (clientId: number) =>
    (await api.get("/invoices/client/" + clientId)).data,
  getByContract: async (contractId: number) =>
    (await api.get("/invoices/contract/" + contractId)).data,
  getByMonth: async (month: string, startDate?: string, endDate?: string) =>
    (
      await api.get("/invoices/month/" + month, {
        params: startDate && endDate ? { startDate, endDate } : {},
      })
    ).data,
  getBillingSummary: async (month?: string) =>
    (
      await api.get("/invoices/billing-summary", {
        params: month ? { month } : {},
      })
    ).data,
  create: async (data: any) => (await api.post("/invoices", data)).data,
  generate: async (data: { contractId: number; referenceMonth: string }) =>
    (await api.post("/invoices/generate", data)).data,
  generateBatch: async (data: {
    referenceMonth: string;
    contractIds?: number[];
  }) => (await api.post("/invoices/generate-batch", data)).data,
  // Fatura consolidada por cliente
  generateConsolidated: async (data: {
    clientId: number;
    referenceMonth: string;
  }) => (await api.post("/invoices/generate-consolidated", data)).data,
  generateBatchConsolidated: async (data: {
    referenceMonth: string;
    clientIds?: number[];
  }) => (await api.post("/invoices/generate-batch-consolidated", data)).data,
  // Re-sincroniza o vencimento das faturas pelo dueDay atual do cliente
  resyncDueDates: async (ids: number[]) =>
    (await api.post("/invoices/resync-due-dates", { ids })).data,
  // Gerar e Emitir (automação completa)
  generateAndEmit: async (data: {
    contractId: number;
    referenceMonth: string;
  }) => (await api.post("/invoices/generate-and-emit", data)).data,
  generateAndEmitConsolidated: async (data: {
    clientId: number;
    referenceMonth: string;
  }) => (await api.post("/invoices/generate-and-emit-consolidated", data)).data,
  update: async (id: number, data: any) =>
    (await api.put("/invoices/" + id, data)).data,
  // force=true só depois da liberação explícita na tela; o backend recusa
  // fatura retida sem isso.
  // `empenhoNote`: o motivo escrito quando a prévia do empenho exige (#468); sem ele o backend recusa com 400.
  finalize: async (id: number, force = false, corpo?: { empenhoNote?: string; releaseNote?: string }) =>
    (
      await api.post(
        "/invoices/" + id + "/finalize" + (force ? "?force=true" : ""),
        corpo || {},
      )
    ).data,
  /** A prévia do empenho na aprovação: um bloco por empenho, os itens sem empenho, os avisos e se exige motivo. */
  getEmpenhoPreview: async (id: number) => (await api.get("/invoices/" + id + "/empenho-preview")).data,
  releaseHold: async (id: number) =>
    (await api.post("/invoices/" + id + "/release-hold")).data,
  cancel: async (id: number) =>
    (await api.post("/invoices/" + id + "/cancel")).data,
  reopen: async (id: number) =>
    (await api.post("/invoices/" + id + "/reopen")).data,
  duplicate: async (id: number, referenceMonth: string) =>
    (await api.post("/invoices/" + id + "/duplicate", { referenceMonth })).data,
  delete: async (id: number) => (await api.delete("/invoices/" + id)).data,
  getPdf: async (id: number) =>
    (await api.get("/invoices/" + id + "/pdf", { timeout: PRAZO_DE_ARQUIVO_MS, responseType: "blob" })).data,
  getPdfBase64: async (id: number) =>
    (await api.get("/invoices/" + id + "/pdf-base64", { timeout: PRAZO_DE_ARQUIVO_MS })).data,
  // Devolve também a ORIGEM do arquivo (oficial do banco ou layout interno): são
  // documentos diferentes e o operador precisa saber qual está olhando.
  getBoletoPdf: async (id: number) => {
    const r = await api.get("/invoices/" + id + "/boleto/pdf", { timeout: PRAZO_DE_ARQUIVO_MS,
      responseType: "blob",
    });
    const origem = (r.headers?.["x-boleto-origem"] as string) || null;
    return Object.assign(r.data as Blob, { origem }) as Blob & {
      origem: string | null;
    };
  },
  getUnifiedPdf: async (id: number) =>
    (
      await api.get("/invoices/" + id + "/unified-pdf", { timeout: PRAZO_DE_ARQUIVO_MS,
        responseType: "blob",
      })
    ).data,
  getPdfToken: async (id: number) =>
    (await api.post("/invoices/" + id + "/pdf-token")).data as {
      token: string;
      url: string;
    },
  getPdfWithAttachment: async (
    id: number,
    attachment?: { data: string; type: "pdf" | "csv"; filename?: string },
  ) =>
    (
      await api.post(
        "/invoices/" + id + "/pdf-with-attachment",
        { attachment },
        { responseType: "blob" },
      )
    ).data,
  getPdfWithAttachmentBase64: async (
    id: number,
    attachment?: { data: string; type: "pdf" | "csv"; filename?: string },
  ) =>
    (
      await api.post("/invoices/" + id + "/pdf-with-attachment-base64", {
        attachment,
      })
    ).data,
  // CDR Processing
  processCdr: async (
    csvContent: string,
    clientFilters?: { [tag: string]: string[] },
  ) =>
    (await api.post("/invoices/cdr/process", { csvContent, clientFilters }))
      .data,
  processCdrWithConfig: async (
    csvContent: string,
    clientClassificationConfigs?: {
      [cdrTag: string]: {
        entrada0800DestinationPatterns?: string[];
        localAreaCode?: string;
      };
    },
    clientFilters?: { [tag: string]: string[] },
  ) =>
    (
      await api.post("/invoices/cdr/process-with-config", {
        csvContent,
        clientClassificationConfigs,
        clientFilters,
      })
    ).data,
  getCdrCsv: async (
    csvContent: string,
    clientTag: string,
    direction: "entrada" | "entrada_0800" | "saida",
  ) =>
    (await api.post("/invoices/cdr/csv", { csvContent, clientTag, direction }))
      .data,
  // Technical Report
  getTechnicalReport: async (invoiceId: number) =>
    (await api.post("/invoices/" + invoiceId + "/technical-report")).data,
  /**
   * URL do PDF do relatório técnico, já com um token de 60s.
   *
   * O PDF é aberto por window.open / fetch sem header, então precisa de uma
   * credencial na própria URL. Antes o endpoint era simplesmente público, o que
   * expunha relatório de qualquer fatura por id sequencial.
   */
  getTechnicalReportPdfUrl: async (invoiceId: number) => {
    const { token } = (
      await api.post("/invoices/" + invoiceId + "/download-token")
    ).data;
    return `${API_URL}/invoices/${invoiceId}/technical-report?dl=${encodeURIComponent(token)}`;
  },
  getTechnicalReportByClient: async (
    clientId: number,
    periodStart: string,
    periodEnd: string,
  ) =>
    (
      await api.post("/invoices/technical-report", {
        clientId,
        periodStart,
        periodEnd,
      })
    ).data,
  // Receivables
  confirmPayment: async (
    id: number,
    data: {
      paymentMethod: string;
      paymentDate: string;
      paidValue: number;
      notes?: string;
    },
  ) => (await api.post("/invoices/" + id + "/confirm-payment", data)).data,
  getReceivablesSummary: async (
    referenceMonth?: string,
    startDate?: string,
    endDate?: string,
  ) =>
    (
      await api.get("/invoices/receivables-summary", {
        params: {
          ...(referenceMonth ? { referenceMonth } : {}),
          ...(startDate && endDate ? { startDate, endDate } : {}),
        },
      })
    ).data,
  exportExcel: async (month?: string, status?: string) =>
    (
      await api.get("/invoices/export", { timeout: PRAZO_DE_ARQUIVO_MS,
        params: { month, status },
        responseType: "blob",
      })
    ).data,
  cancelDiscount: async (id: number) =>
    (await api.post("/invoices/" + id + "/cancel-discount")).data,
  restoreDiscount: async (id: number) =>
    (await api.post("/invoices/" + id + "/restore-discount")).data,
  // O que foi mandado em cada disparo (corpo, nota, anexos). Ver utils/comprovanteDeEnvio.
  getComprovantesDeEnvio: async (id: number) =>
    (await api.get("/invoices/" + id + "/comprovantes-de-envio")).data,
  // Email sending
  sendEmail: async (id: number, emails?: string[], customNote?: string) =>
    (
      await api.post("/invoices/" + id + "/send-email", {
        ...(emails?.length ? { emails } : {}),
        ...(customNote ? { customNote } : {}),
      })
    ).data,
  validateBoletos: async (invoiceIds: number[]) =>
    (await api.post("/invoices/validate-boletos", { invoiceIds })).data,
  sendEmailBatch: async (invoiceIds: number[], customNote?: string) =>
    (
      await api.post("/invoices/send-email-batch", {
        invoiceIds,
        ...(customNote ? { customNote } : {}),
      })
    ).data,
  finalizeBatch: async (invoiceIds: number[]) =>
    (await api.post("/invoices/finalize-batch", { invoiceIds })).data,
  getBatchProgress: async (jobId: string) =>
    (await api.get("/invoices/batch-progress/" + jobId)).data,
  sendBoletoCorrectionBatch: async (invoiceIds: number[]) =>
    (await api.post("/invoices/send-boleto-correction-batch", { invoiceIds }))
      .data,

  // ── Cobrança de fatura vencida (régua de lembretes)
  /** Faturas vencidas com o estado da cobrança e o motivo de quem está fora. */
  dunningCandidates: async () =>
    (await api.get("/invoices/dunning-candidates")).data,
  /** HTML exato que o cliente receberia, sem enviar nada. */
  dunningPreview: async (
    invoiceId: number,
    level?: 1 | 2 | 3,
    includeCharges = true,
  ) =>
    (
      await api.get(`/invoices/${invoiceId}/dunning-preview`, {
        params: { level, includeCharges },
      })
    ).data,
  sendDunning: async (
    invoiceId: number,
    body: {
      level?: 1 | 2 | 3;
      emails?: string[];
      customNote?: string;
      includeCharges?: boolean;
    },
  ) => (await api.post(`/invoices/${invoiceId}/send-dunning`, body)).data,
  sendDunningBatch: async (body: {
    invoiceIds: number[];
    level?: 1 | 2 | 3;
    customNote?: string;
    includeCharges?: boolean;
    /** Ensaio: manda para `testEmails` e não grava histórico. */
    test?: boolean;
    testEmails?: string[];
  }) => (await api.post("/invoices/send-dunning-batch", body)).data,
  /** Silencia a cobrança de uma fatura. `until` nulo reativa. */
  snoozeDunning: async (
    invoiceId: number,
    until: string | null,
    reason: string | null,
  ) =>
    (
      await api.post(`/invoices/${invoiceId}/dunning-snooze`, {
        until: until || undefined,
        reason: reason || undefined,
      })
    ).data,

  // ── Cobrança fase 1 (backend #569; contrato em docs/cobranca-fase-1-contrato.md do backend)
  /** A fila com os seis motivos na linha (`?motivos=1`): nota autorizada, entrega, promessa vencida e viva, órgão/TED. */
  dunningQueue: async () =>
    (await api.get("/invoices/dunning-candidates", { params: { motivos: 1 } }))
      .data,
  /**
   * O resumo da MESMA consulta da fila (backend #587): `aReceber`, `vencidas` e o `aging` nas faixas K1
   * (`dias1a30`, `dias31a60`, `dias61a90`, `dias90mais`, sempre presentes), com `count` e `value` (saldo em aberto).
   * Mesmo guard da lista. É o cabeçalho da Cobrança e o cartão Inadimplência do Dashboard.
   */
  dunningQueueSummary: async () =>
    (await api.get("/invoices/dunning-candidates/summary")).data,
  /**
   * A geração mensal em prévia (03/11/2026): período, linhas MRR já recortadas por vigência e itens fora do período,
   * sem gravar nada. Com `contractId`, as linhas do contrato; só com `clientId`, só o período. É o que o modal "Nova
   * fatura" propõe, para não ter regra própria. Ver utils/previaDaGeracao.
   */
  generationPreview: async (params: { referenceMonth: string; contractId?: number; clientId?: number }) =>
    (await api.get("/invoices/generation-preview", { params })).data,
  /** A trilha de contatos da fatura, mais nova primeiro, e a promessa que está valendo. */
  getContacts: async (invoiceId: number) =>
    (await api.get(`/invoices/${invoiceId}/contacts`)).data,
  /** Registra uma conversa. O autor sai do token; `promisedDate`/`promisedAmount` são opcionais e independentes. */
  registerContact: async (
    invoiceId: number,
    body: {
      contactedAt?: string;
      channel: string;
      clientSaid?: string;
      promisedDate?: string;
      promisedAmount?: number;
    },
  ) => (await api.post(`/invoices/${invoiceId}/contacts`, body)).data,
  /** Carimba o desfecho da promessa. Voltar para pendente o backend recusa. */
  setPromiseOutcome: async (
    contactId: number,
    status: "cumprida" | "nao_cumprida",
  ) =>
    (await api.patch(`/invoices/contacts/${contactId}/promise`, { status }))
      .data,
  /** O placar do cliente: promessas cumpridas, não cumpridas e pendentes. */
  promiseStats: async (clientId: number) =>
    (await api.get(`/invoices/clients/${clientId}/promise-stats`)).data,
  /** A pendência de entrega (e-mail devolvido) e os destinos alternativos para o reenvio. */
  deliveryIssues: async (invoiceId: number) =>
    (await api.get(`/invoices/${invoiceId}/delivery-issues`)).data,
  /** Reenvia a cobrança para um dos destinos alternativos e resolve a pendência `emailLogId`. */
  resendDunning: async (invoiceId: number, to: string, emailLogId: number) =>
    (
      await api.post(`/invoices/${invoiceId}/resend-dunning`, {
        to,
        emailLogId,
      })
    ).data,

  // ── Cobrança fase 2 (backend #571; contrato em docs/cobranca-fase-2-contrato.md do backend)
  /** A fila de sugestões num status (`sugerida` por padrão), com o placar. */
  dunningSuggestions: async (status?: string) =>
    (await api.get("/invoices/dunning-suggestions", { params: status ? { status } : {} }))
      .data,
  /** O que a última montagem NÃO sugeriu, fatura por fatura, com o código e a frase do servidor. */
  dunningSuggestionsHeld: async () =>
    (await api.get("/invoices/dunning-suggestions/retidas")).data,
  /** O texto final e os destinatários, pelo mesmo caminho do envio. ADMIN e EDITOR. */
  dunningSuggestionPreview: async (id: number) =>
    (await api.get(`/invoices/dunning-suggestions/${id}/preview`)).data,
  /** Aprova e executa uma sugestão. Recusa vem `200` com `sent: false` e `error`. ADMIN. */
  approveDunningSuggestion: async (id: number) =>
    (await api.post(`/invoices/dunning-suggestions/${id}/approve`)).data,
  /** Um claim e um envio por sugestão; devolve o resultado de cada uma. ADMIN. */
  approveDunningSuggestions: async (ids: number[]) =>
    (await api.post("/invoices/dunning-suggestions/approve-batch", { ids })).data,
  /** Descarta com motivo obrigatório. ADMIN e EDITOR. */
  discardDunningSuggestion: async (id: number, reason: string) =>
    (await api.post(`/invoices/dunning-suggestions/${id}/discard`, { reason })).data,
};

export const clientDDRsService = {
  getAll: async () => (await api.get("/client-ddrs")).data,
  getById: async (id: number) => (await api.get("/client-ddrs/" + id)).data,
  getByClient: async (clientId: number) =>
    (await api.get("/client-ddrs/client/" + clientId)).data,
  getByCarrier: async (carrier: string) =>
    (await api.get("/client-ddrs/carrier/" + carrier)).data,
  getStats: async () => (await api.get("/client-ddrs/stats")).data,
  search: async (phone: string) =>
    (await api.get("/client-ddrs/search", { params: { phone } })).data,
  // Prévia da derivação do número (cidade/UF/CNL/Área Local) pela base STFC, sem salvar.
  previewCnl: async (numero: string) =>
    (await api.get("/client-ddrs/preview-cnl", { params: { numero } }))
      .data as {
      found: boolean;
      cnl: string | null;
      cnlLocalidade: string | null;
      cnlAreaTarifacao: string | null;
      city: string | null;
      state: string | null;
      areaLocal: string | null;
      areaLocalCodigo: string | null;
    },
  // Consulta a operadora REAL (portabilidade) na ABR Consulta Número — resolve
  // o captcha no backend (~60s). Retorna a prestadora atual do número.
  consultarOperadora: async (numero: string) =>
    (
      await api.get("/client-ddrs/consultar-operadora", {
        params: { numero },
        timeout: 200000,
      })
    ).data as {
      found: boolean;
      prestadora: string | null;
      razaoSocial: string | null;
      consultadoEm: string | null;
      telefone: string;
    },
  create: async (data: any) => (await api.post("/client-ddrs", data)).data,
  update: async (id: number, data: any) =>
    (await api.put("/client-ddrs/" + id, data)).data,
  updateStatus: async (id: number, status: string) =>
    (await api.put("/client-ddrs/" + id + "/status", { status })).data,
  delete: async (id: number) => (await api.delete("/client-ddrs/" + id)).data,
  bulkCreate: async (baseData: any, rangeStart: number, rangeEnd: number) =>
    (
      await api.post("/client-ddrs/bulk-create", {
        baseData,
        rangeStart,
        rangeEnd,
      })
    ).data,
  bulkDelete: async (ids: number[]) =>
    (await api.post("/client-ddrs/bulk-delete", { ids })).data,
  transfer: async (ddrIds: number[], newClientId: number) =>
    (await api.post("/client-ddrs/transfer", { ddrIds, newClientId })).data,
  transferToLocation: async (ddrIds: number[], locationId: number | null) =>
    (await api.post("/client-ddrs/transfer-location", { ddrIds, locationId }))
      .data,
  bulkUpdateCarriers: async (
    ddrIds: number[],
    data: {
      carrierId?: number | null;
      hostCarrierId?: number | null;
      technology?: string;
      techPrefix?: string | null;
      appUrl?: string | null;
      accessAddressId?: number | null;
      channels?: number | null;
    },
  ) =>
    (await api.post("/client-ddrs/bulk-update-carriers", { ddrIds, ...data }))
      .data,
  import: async (data: any[]) =>
    (await api.post("/client-ddrs/import", { data })).data,
};

/** Um endereço de acesso (host do PABX/SBC/APP do cliente), E1. `clientes`/`ddrs` só vêm na lista geral. */
export interface EnderecoDeAcesso {
  id: number;
  host: string;
  note?: string | null;
  clientes?: number;
  ddrs?: number;
}

/**
 * Endereços de acesso (E1, backend #514). A lista é de qualquer autenticado; criar é ADMIN. Host repetido volta 409
 * ENDERECO_JA_EXISTE com o `id` do que já existe; host inválido, 400 HOST_INVALIDO.
 */
export const accessAddressesService = {
  listar: async (): Promise<EnderecoDeAcesso[]> => (await api.get("/access-addresses")).data,
  criar: async (host: string): Promise<EnderecoDeAcesso> => (await api.post("/access-addresses", { host })).data,
};

export const emailTemplatesService = {
  getAll: async () => (await api.get("/email-templates")).data,
  getById: async (id: number) => (await api.get("/email-templates/" + id)).data,
  getByType: async (type: string) =>
    (await api.get("/email-templates/by-type/" + type)).data,
  getDefaultByType: async (type: string) =>
    (await api.get("/email-templates/default/" + type)).data,
  create: async (data: any) => (await api.post("/email-templates", data)).data,
  update: async (id: number, data: any) =>
    (await api.put("/email-templates/" + id, data)).data,
  delete: async (id: number) =>
    (await api.delete("/email-templates/" + id)).data,
  duplicate: async (id: number) =>
    (await api.post("/email-templates/" + id + "/duplicate")).data,
  seed: async () => (await api.post("/email-templates/seed")).data,
};

export const communicationsService = {
  sendBatch: async (payload: {
    clientIds: number[];
    recipients?: Array<{ clientId: number; email: string; name?: string }>;
    subject: string;
    htmlContent: string;
    purpose?: string;
    attachments?: Array<{
      filename: string;
      contentBase64: string;
      contentType?: string;
    }>;
    // O comprovante (backend do Bastião, 27/10/2026): qual modelo, se foi mexido, e o teste que cobre o conteúdo.
    templateId?: number;
    templateName?: string;
    templateEditado?: boolean;
    testeId?: string;
  }) => (await api.post("/communications/send-batch", payload)).data,
  getBatchProgress: async (jobId: string) =>
    (await api.get("/communications/batch-progress/" + jobId)).data,
  /** A prévia pelo MESMO render do envio (até 3 destinatários; não envia nada). */
  preview: async (payload: {
    subject: string;
    htmlContent: string;
    recipients: Array<{ clientId: number; email: string; name?: string }>;
  }): Promise<Array<{ clientId: number; clientName: string; recipientEmail: string; subject: string; html: string }>> =>
    (await api.post("/communications/preview", payload)).data,
  /** O teste para o próprio usuário, com [TESTE] no assunto; acima de `limiteSemTeste` o lote exige o `testeId`. */
  sendTest: async (payload: {
    subject: string;
    htmlContent: string;
    attachments?: Array<{ filename: string; contentBase64: string; contentType?: string }>;
    templateId?: number;
    templateName?: string;
    templateEditado?: boolean;
    amostraClientId?: number;
  }): Promise<{ testeId: string; enviadoPara: string; hashDoConteudo: string; enviado: boolean; emailLogId?: number; amostraDe?: string; limiteSemTeste: number }> =>
    (await api.post("/communications/send-test", payload)).data,
};

export const contractTemplatesService = {
  getAll: async () => (await api.get("/contract-templates")).data,
  getActive: async () => (await api.get("/contract-templates/active")).data,
  getDefault: async () => (await api.get("/contract-templates/default")).data,
  getById: async (id: number) =>
    (await api.get("/contract-templates/" + id)).data,
  create: async (data: any) =>
    (await api.post("/contract-templates", data)).data,
  update: async (id: number, data: any) =>
    (await api.patch("/contract-templates/" + id, data)).data,
  delete: async (id: number) =>
    (await api.delete("/contract-templates/" + id)).data,
};

// AWS Reservations
export const awsReservationsService = {
  // Reservations
  getAll: async () => (await api.get("/aws-reservations")).data,
  getById: async (id: number) =>
    (await api.get("/aws-reservations/" + id)).data,
  getActive: async () => (await api.get("/aws-reservations/active")).data,
  getExpiring: async (days: number) =>
    (await api.get("/aws-reservations/expiring/" + days)).data,
  getDashboard: async () => (await api.get("/aws-reservations/dashboard")).data,
  getUtilization: async () =>
    (await api.get("/aws-reservations/utilization")).data,
  getCoverage: async () => (await api.get("/aws-reservations/coverage")).data,
  create: async (data: any) => (await api.post("/aws-reservations", data)).data,
  update: async (id: number, data: any) =>
    (await api.put("/aws-reservations/" + id, data)).data,
  delete: async (id: number) =>
    (await api.delete("/aws-reservations/" + id)).data,
  linkInstance: async (reservationId: number, instanceId: string) =>
    (
      await api.post("/aws-reservations/" + reservationId + "/link-instance", {
        instanceId,
      })
    ).data,
  unlinkInstance: async (reservationId: number, instanceId: string) =>
    (
      await api.post(
        "/aws-reservations/" + reservationId + "/unlink-instance",
        { instanceId },
      )
    ).data,
  dismissAlert: async (id: number) =>
    (await api.post("/aws-reservations/" + id + "/dismiss-alert")).data,
  // Sync
  sync: async (options?: { regions?: string[]; includeExpired?: boolean }) =>
    (await api.post("/aws-reservations/sync", options || {})).data,
  syncReservations: async (options?: {
    regions?: string[];
    includeExpired?: boolean;
  }) =>
    (await api.post("/aws-reservations/sync/reservations", options || {})).data,
  syncInstances: async (options?: { regions?: string[] }) =>
    (await api.post("/aws-reservations/sync/instances", options || {})).data,
};

export const awsInstancesService = {
  getAll: async () => (await api.get("/aws-reservations/instances")).data,
  getById: async (id: number) =>
    (await api.get("/aws-reservations/instances/" + id)).data,
  getUnreserved: async () =>
    (await api.get("/aws-reservations/instances/unreserved")).data,
  getByType: async (type: string) =>
    (await api.get("/aws-reservations/instances/by-type/" + type)).data,
  getByClient: async (clientId: number) =>
    (await api.get("/aws-reservations/instances/by-client/" + clientId)).data,
  getStatsByType: async () =>
    (await api.get("/aws-reservations/instances/stats/by-type")).data,
  getStatsByRegion: async () =>
    (await api.get("/aws-reservations/instances/stats/by-region")).data,
  update: async (id: number, data: any) =>
    (await api.put("/aws-reservations/instances/" + id, data)).data,
  linkToClient: async (instanceId: number, clientId: number) =>
    (
      await api.post(
        "/aws-reservations/instances/" +
          instanceId +
          "/link-client/" +
          clientId,
      )
    ).data,
  unlinkFromClient: async (instanceId: number) =>
    (
      await api.post(
        "/aws-reservations/instances/" + instanceId + "/unlink-client",
      )
    ).data,
  getAllClients: async () =>
    (await api.get("/aws-reservations/instances/all-clients")).data,
  associateClient: async (instanceId: string, clientId: number) =>
    (
      await api.post("/aws-reservations/instances/associate-client", {
        instanceId,
        clientId,
      })
    ).data,
  disassociateClient: async (clientId: number) =>
    (
      await api.post(
        "/aws-reservations/instances/disassociate-client/" + clientId,
      )
    ).data,
};

// === OFICIOS ===
export const oficiosService = {
  getAll: async (filters?: {
    ano?: number;
    tipo?: string;
    status?: string;
    clientId?: number;
    contractId?: number;
  }) => {
    const params = new URLSearchParams();
    if (filters?.ano) params.append("ano", String(filters.ano));
    if (filters?.tipo) params.append("tipo", filters.tipo);
    if (filters?.status) params.append("status", filters.status);
    if (filters?.clientId) params.append("clientId", String(filters.clientId));
    if (filters?.contractId)
      params.append("contractId", String(filters.contractId));
    const query = params.toString();
    return (await api.get("/oficios" + (query ? "?" + query : ""))).data;
  },
  getById: async (id: number) => (await api.get("/oficios/" + id)).data,
  create: async (data: any) => (await api.post("/oficios", data)).data,
  update: async (id: number, data: any) =>
    (await api.patch("/oficios/" + id, data)).data,
  delete: async (id: number) => (await api.delete("/oficios/" + id)).data,
  emit: async (id: number) => (await api.post("/oficios/" + id + "/emit")).data,
  cancel: async (id: number) =>
    (await api.post("/oficios/" + id + "/cancel")).data,
  reopen: async (id: number) =>
    (await api.post("/oficios/" + id + "/reopen")).data,
  // Revogar exige motivo: o backend recusa com menos de 10 caracteres.
  revoke: async (id: number, motivo: string) =>
    (await api.post("/oficios/" + id + "/revoke", { motivo })).data,
  resendNotification: async (id: number) =>
    (await api.post("/oficios/" + id + "/resend-notification")).data,
  // PDF
  getPdfBase64: async (id: number) =>
    (await api.get("/oficios/" + id + "/pdf-base64", { timeout: PRAZO_DE_ARQUIVO_MS })).data,
  getPdf: async (id: number) =>
    (await api.get("/oficios/" + id + "/pdf", { timeout: PRAZO_DE_ARQUIVO_MS, responseType: "blob" })).data,
  // Assinatura
  sendForSignature: async (
    id: number,
    signers: Array<{
      name: string;
      email: string;
      cpf?: string;
      birthday?: string;
      phone?: string;
      role?: string;
      authType?: string;
    }>,
    mergeAttachments?: boolean,
  ) =>
    (
      await api.post("/oficios/" + id + "/send-for-signature", {
        signers,
        mergeAttachments,
      })
    ).data,
  getSignatureStatus: async (id: number) =>
    (await api.get("/oficios/" + id + "/signature-status")).data,
  // Timeline do modal do olho (criacao, emissao, envio, e-mails, assinaturas, revogacao).
  getHistory: async (id: number) =>
    (await api.get("/oficios/" + id + "/history")).data,
  // Anexos
  addAttachment: async (
    id: number,
    data: { filename: string; fileBase64: string; uploadedBy?: string },
  ) => (await api.post("/oficios/" + id + "/attachments", data)).data,
  removeAttachment: async (id: number, attachmentId: string) =>
    (await api.delete("/oficios/" + id + "/attachments/" + attachmentId)).data,
  downloadAttachment: async (
    id: number,
    attachmentId: string,
    filename: string,
  ) => {
    const response = await api.get(
      "/oficios/" + id + "/attachments/" + attachmentId + "/download",
      {
        responseType: "blob",
      },
    );
    const blob = new Blob([response.data], {
      type: response.headers["content-type"],
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
  viewAttachment: async (id: number, attachmentId: string) => {
    const response = await api.get(
      "/oficios/" + id + "/attachments/" + attachmentId + "/download",
      {
        responseType: "blob",
      },
    );
    const blob = new Blob([response.data], {
      type: response.headers["content-type"],
    });
    const url = window.URL.createObjectURL(blob);
    window.open(url, "_blank");
  },
  // Auxiliares
  getTipos: async () => (await api.get("/oficios/tipos")).data,
  getYears: async () => (await api.get("/oficios/years")).data,
  getStats: async (ano?: number) =>
    (await api.get("/oficios/stats" + (ano ? "?ano=" + ano : ""))).data,
  getTemplate: async (tipo: string) =>
    (await api.get("/oficios/template/" + encodeURIComponent(tipo))).data,
};

// === DEMANDAS ===
export const demandasService = {
  getAll: async (filters?: {
    status?: string;
    system?: string;
    type?: string;
    priority?: string;
    assigneeId?: number;
    search?: string;
    includeClosed?: boolean;
  }) => {
    const params = new URLSearchParams();
    if (filters?.status) params.append("status", filters.status);
    if (filters?.system) params.append("system", filters.system);
    if (filters?.type) params.append("type", filters.type);
    if (filters?.priority) params.append("priority", filters.priority);
    if (filters?.assigneeId)
      params.append("assigneeId", String(filters.assigneeId));
    if (filters?.search) params.append("search", filters.search);
    if (filters?.includeClosed) params.append("includeClosed", "true");
    const query = params.toString();
    return (await api.get("/demandas" + (query ? "?" + query : ""))).data;
  },
  getById: async (id: number) => (await api.get("/demandas/" + id)).data,
  create: async (data: any) => (await api.post("/demandas", data)).data,
  update: async (id: number, data: any) =>
    (await api.patch("/demandas/" + id, data)).data,
  changeStatus: async (id: number, status: string, comment?: string) =>
    (await api.patch("/demandas/" + id + "/status", { status, comment })).data,
  changeAssignee: async (id: number, assigneeId: number | null) =>
    (await api.patch("/demandas/" + id + "/assignee", { assigneeId })).data,
  delete: async (id: number) => (await api.delete("/demandas/" + id)).data,
  countOpen: async () => (await api.get("/demandas/count-open")).data,
  getUsers: async () => (await api.get("/demandas/users")).data,
  // Comentários
  addComment: async (id: number, body: string) =>
    (await api.post("/demandas/" + id + "/comments", { body })).data,
  // Anexos
  addAttachment: async (
    id: number,
    data: { filename: string; fileBase64: string; commentId?: number },
  ) => (await api.post("/demandas/" + id + "/attachments", data)).data,
  removeAttachment: async (id: number, attachmentId: number) =>
    (await api.delete("/demandas/" + id + "/attachments/" + attachmentId)).data,
  // Baixa o anexo autenticado e devolve um object URL (thumbnail/preview)
  getAttachmentBlobUrl: async (id: number, attachmentId: number) => {
    const response = await api.get(
      "/demandas/" + id + "/attachments/" + attachmentId + "/download",
      {
        responseType: "blob",
      },
    );
    const blob = new Blob([response.data], {
      type: response.headers["content-type"],
    });
    return window.URL.createObjectURL(blob);
  },
  viewAttachment: async (id: number, attachmentId: number) => {
    const url = await demandasService.getAttachmentBlobUrl(id, attachmentId);
    window.open(url, "_blank");
  },
  downloadAttachment: async (
    id: number,
    attachmentId: number,
    filename: string,
  ) => {
    const url = await demandasService.getAttachmentBlobUrl(id, attachmentId);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
};

// === MATERIALS ===
export const materialsService = {
  getAll: async () => (await api.get("/materials")).data,
  getById: async (id: number) => (await api.get("/materials/" + id)).data,
  getStats: async () => (await api.get("/materials/stats")).data,
  upload: async (
    file: File,
    data: {
      title: string;
      description?: string;
      category?: string;
      status?: string;
      tags?: string[];
    },
  ) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("title", data.title);
    if (data.description) formData.append("description", data.description);
    if (data.category) formData.append("category", data.category);
    if (data.status) formData.append("status", data.status);
    if (data.tags) formData.append("tags", JSON.stringify(data.tags));
    return (
      await api.post("/materials/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    ).data;
  },
  update: async (
    id: number,
    data: {
      title?: string;
      description?: string;
      category?: string;
      status?: string;
      tags?: string[];
      isPublic?: boolean;
    },
  ) => (await api.put("/materials/" + id, data)).data,
  delete: async (id: number) => (await api.delete("/materials/" + id)).data,
  createPublicLink: async (id: number, days?: number) =>
    (await api.post("/materials/" + id + "/public-link", { days })).data as {
      url: string;
      publicToken: string;
      expiresAt: string | null;
    },
  revokePublicLink: async (id: number) =>
    (await api.delete("/materials/" + id + "/public-link")).data,
  getDownloadUrl: async (id: number) =>
    (await api.get("/materials/" + id + "/download")).data,
  uploadVersion: async (id: number, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return (
      await api.post("/materials/" + id + "/version", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    ).data;
  },
  // Version history
  getVersions: async (id: number) =>
    (await api.get("/materials/" + id + "/versions")).data,
  getVersionDownloadUrl: async (id: number, version: number) =>
    (await api.get("/materials/" + id + "/versions/" + version + "/download"))
      .data,
  downloadVersion: async (id: number, version: number) => {
    const { url, fileName } = await materialsService.getVersionDownloadUrl(
      id,
      version,
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  },
  // Download helper
  download: async (id: number) => {
    const { url, fileName } = await materialsService.getDownloadUrl(id);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  },
  // Public access (no auth needed)
  getPublic: async (code: string) => {
    const response = await fetch(
      `${api.defaults.baseURL}/materials/public/${code}`,
    );
    if (!response.ok) throw new Error("Material nao encontrado");
    return response.json();
  },
  getPublicDownloadUrl: (code: string) =>
    `${api.defaults.baseURL}/materials/public/${code}/download`,
};

// === CORPORATE DOCS ===
export const corporateDocsService = {
  getAll: async () => (await api.get("/corporate-docs")).data,
  getById: async (id: number) => (await api.get("/corporate-docs/" + id)).data,
  upload: async (
    file: File,
    data: {
      title: string;
      description?: string;
      category?: string;
      notes?: string;
      effectiveDate?: string;
      expirationDate?: string;
    },
  ) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("title", data.title);
    if (data.description) formData.append("description", data.description);
    if (data.category) formData.append("category", data.category);
    if (data.notes) formData.append("notes", data.notes);
    if (data.effectiveDate)
      formData.append("effectiveDate", data.effectiveDate);
    if (data.expirationDate)
      formData.append("expirationDate", data.expirationDate);
    return (
      await api.post("/corporate-docs/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    ).data;
  },
  update: async (id: number, data: any) =>
    (await api.put("/corporate-docs/" + id, data)).data,
  delete: async (id: number) =>
    (await api.delete("/corporate-docs/" + id)).data,
  approve: async (id: number) =>
    (await api.post("/corporate-docs/" + id + "/approve")).data,
  changeStatus: async (id: number, data: { status: string; notes?: string }) =>
    (await api.post("/corporate-docs/" + id + "/status", data)).data,
  getDownloadUrl: async (id: number) =>
    (await api.get("/corporate-docs/" + id + "/download")).data,
  uploadVersion: async (id: number, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return (
      await api.post("/corporate-docs/" + id + "/version", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    ).data;
  },
  download: async (id: number) => {
    const { url, fileName } = await corporateDocsService.getDownloadUrl(id);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  },
  sendForSignature: async (id: number, signers: any[]) =>
    (
      await api.post("/corporate-docs/" + id + "/send-for-signature", {
        signers,
      })
    ).data,
  getSignatureStatus: async (id: number) =>
    (await api.get("/corporate-docs/" + id + "/signature-status")).data,
  cancelSignature: async (id: number) =>
    (await api.post("/corporate-docs/" + id + "/cancel-signature")).data,
  resendNotification: async (id: number, signerKey: string) =>
    (
      await api.post("/corporate-docs/" + id + "/resend-notification", {
        signerKey,
      })
    ).data,
  getSignedPdfUrl: async (id: number) =>
    (await api.get("/corporate-docs/" + id + "/signed-pdf")).data,
  // Timeline do modal do olho, mesmo formato de oficios e portabilidade.
  getHistory: async (id: number) =>
    (await api.get("/corporate-docs/" + id + "/history")).data,
  downloadSignedPdf: async (id: number) => {
    const { url, fileName } = await corporateDocsService.getSignedPdfUrl(id);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  },
};

// === SYSTEM CREDENTIALS ===
export const systemCredentialsService = {
  getAll: async () => (await api.get("/system-credentials")).data,
  getDashboard: async () =>
    (await api.get("/system-credentials/dashboard")).data,
  getAlerts: async () => (await api.get("/system-credentials/alerts")).data,
  checkConnections: async () =>
    (await api.get("/system-credentials/check-connections")).data,
  checkAws: async () => (await api.get("/system-credentials/check/aws")).data,
  checkSmtp: async () => (await api.get("/system-credentials/check/smtp")).data,
  checkClicksign: async () =>
    (await api.get("/system-credentials/check/clicksign")).data,
  checkNuvemFiscal: async () =>
    (await api.get("/system-credentials/check/nuvem-fiscal")).data,
  getById: async (id: number) =>
    (await api.get("/system-credentials/" + id)).data,
  getByType: async (type: string) =>
    (await api.get("/system-credentials/type/" + type)).data,
  create: async (data: any) =>
    (await api.post("/system-credentials", data)).data,
  update: async (id: number, data: any) =>
    (await api.put("/system-credentials/" + id, data)).data,
  delete: async (id: number) =>
    (await api.delete("/system-credentials/" + id)).data,
  seed: async () => (await api.post("/system-credentials/seed")).data,
};

// === SMTP CONFIGS ===
export const smtpConfigsService = {
  getAll: async () => (await api.get("/smtp-configs")).data,
  getById: async (id: number) => (await api.get("/smtp-configs/" + id)).data,
  create: async (data: any) => (await api.post("/smtp-configs", data)).data,
  update: async (id: number, data: any) =>
    (await api.put("/smtp-configs/" + id, data)).data,
  delete: async (id: number) => (await api.delete("/smtp-configs/" + id)).data,
  testConnection: async (id: number) =>
    (await api.post("/smtp-configs/" + id + "/test")).data,
  migrateFromEnv: async () =>
    (await api.post("/smtp-configs/migrate-from-env")).data,
};

// === SYSTEM (Company Settings) ===
export const systemService = {
  getCompanySettings: async () => (await api.get("/system/company")).data,
  updateCompanySettings: async (data: any) =>
    (await api.put("/system/company", data)).data,
  // Public access (no auth needed)
  getCompanySettingsPublic: async () => {
    const response = await fetch(`${api.defaults.baseURL}/system/company`);
    if (!response.ok) throw new Error("Erro ao carregar dados da empresa");
    return response.json();
  },
};

// === FISCAL ===
export const fiscalService = {
  // Documentos fiscais
  getAll: async (params?: any) =>
    (await api.get("/fiscal/documents", { params })).data,
  getById: async (id: number) =>
    (await api.get("/fiscal/documents/" + id)).data,
  delete: async (id: number) =>
    (await api.delete("/fiscal/documents/" + id)).data,
  getByInvoice: async (invoiceId: number) =>
    (await api.get("/fiscal/documents/invoice/" + invoiceId)).data,
  getDashboard: async () => (await api.get("/fiscal/documents/dashboard")).data,
  previewSplit: async (invoiceId: number) =>
    (await api.get("/fiscal/documents/preview-split/" + invoiceId)).data,
  splitInvoice: async (invoiceId: number) =>
    (await api.post("/fiscal/documents/split/" + invoiceId)).data,
  // O empenho vai junto: alem de virar sufixo no nome dos itens (unica forma de
  // sair na NFS-e), ele fica gravado na FATURA e e o que separa "esperando o
  // orgao" de "esperando a gente" na pendencia de NF manual.
  // Registra o numero do empenho na fatura SEM emitir. O numero chega por
  // e-mail ou WhatsApp dias antes de alguem abrir a tela de emissao.
  registrarEmpenho: async (invoiceId: number, empenhoNumber: string) =>
    (await api.post("/fiscal/documents/empenho/" + invoiceId, { empenhoNumber })).data,
  createPartialNfse: async (invoiceId: number, items: any[], empenhoNumber?: string) =>
    (await api.post("/fiscal/documents/partial-nfse/" + invoiceId, { items, empenhoNumber }))
      .data,
  deleteByInvoice: async (invoiceId: number) =>
    (await api.delete("/fiscal/documents/invoice/" + invoiceId)).data,
  getCancellationReasons: async () =>
    (await api.get("/fiscal/documents/cancellation-reasons")).data,

  // NFS-e
  transmitNfse: async (id: number) =>
    (await api.post("/fiscal/nfse/transmit/" + id)).data,
  transmitNfseBatch: async (ids: number[]) =>
    (await api.post("/fiscal/nfse/transmit-batch", { ids })).data,
  checkNfseStatus: async (id: number) =>
    (await api.get("/fiscal/nfse/status/" + id)).data,
  cancelNfse: async (id: number, reason: string) =>
    (await api.post("/fiscal/nfse/cancel/" + id, { reason })).data,
  retryNfse: async (id: number) =>
    (await api.post("/fiscal/nfse/retry/" + id)).data,
  downloadNfsePdf: async (id: number) =>
    (await api.get("/fiscal/nfse/pdf/" + id, { timeout: PRAZO_DE_ARQUIVO_MS })).data,

  // NFCom
  transmitNfcom: async (id: number) =>
    (await api.post("/fiscal/nfcom/transmit/" + id)).data,
  transmitNfcomBatch: async (ids: number[]) =>
    (await api.post("/fiscal/nfcom/transmit-batch", { ids })).data,
  checkNfcomStatus: async (id: number) =>
    (await api.get("/fiscal/nfcom/status/" + id)).data,
  cancelNfcom: async (id: number, reason: string) =>
    (await api.post("/fiscal/nfcom/cancel/" + id, { reason })).data,
  retryNfcom: async (id: number) =>
    (await api.post("/fiscal/nfcom/retry/" + id)).data,
  downloadNfcomPdf: async (id: number) =>
    (await api.get("/fiscal/nfcom/pdf/" + id, { timeout: PRAZO_DE_ARQUIVO_MS })).data,
  sefazStatus: async () => (await api.get("/fiscal/nfcom/service-status")).data,
  getFustFunttelSummary: async (referenceMonth: string) =>
    (
      await api.get("/fiscal/documents/fust-funttel", {
        params: { referenceMonth },
      })
    ).data,
  getMonitoring: async (period?: string) =>
    (await api.get("/fiscal/documents/monitoring", { params: { period } }))
      .data,
  exportExcel: async (
    type?: string,
    status?: string,
    referenceMonth?: string,
  ) =>
    (
      await api.get("/fiscal/documents/export", { timeout: PRAZO_DE_ARQUIVO_MS,
        params: { type, status, referenceMonth },
        responseType: "blob",
      })
    ).data,
  downloadZip: async (ids: number[]) => {
    const response = await api.post(
      "/fiscal/documents/download-zip",
      { ids },
      { responseType: "blob" },
    );
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = "Documentos_Fiscais.zip";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
  downloadXmlZip: async (
    referenceMonth: string,
    type: "nfse" | "nfcom" | "all" = "all",
  ) => {
    const label =
      type === "all" ? "XMLs" : type === "nfse" ? "XMLs_NFSe" : "XMLs_NFCom";
    const response = await api.post(
      "/fiscal/documents/download-xml-zip",
      { referenceMonth, type },
      { responseType: "blob" },
    );
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${label}_${referenceMonth}.zip`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
  downloadXml: async (id: number, fallbackName = `NF_${id}.xml`) => {
    const response = await api.get(`/fiscal/documents/${id}/xml`, {
      responseType: "blob",
    });
    // O backend manda o nome bom (tipo + nº da nota + cliente) no Content-Disposition;
    // o header só chega porque o backend expõe ele no Access-Control-Expose-Headers.
    const disposition = response.headers["content-disposition"] as
      string | undefined;
    const match = disposition?.match(/filename="?([^"]+)"?/);
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = match?.[1] || fallbackName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
};

export const certificateService = {
  getAll: async () => (await api.get("/fiscal/certificates")).data,
  getActiveInfo: async () =>
    (await api.get("/fiscal/certificates/active/info")).data,
  upload: async (formData: FormData) =>
    (
      await api.post("/fiscal/certificates/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    ).data,
  delete: async (id: number) =>
    (await api.delete("/fiscal/certificates/" + id)).data,
};

// === CNAB / BOLETOS ===
// A emissão por remessa CNAB (`POST /cnab/remessa`) não é mais exposta aqui:
// todo boleto nasce pela API do Sicredi (`emitirHibrido`), que registra na hora
// com QR PIX. O endpoint segue no backend como contingência para API fora,
// exigindo `contingencia: true` e perfil ADMIN.
export const cnabService = {
  // Baixa em lote pela API: imediata no banco, sem arquivo para subir no
  // portal. `generateRemessaBaixa` fica como contingência para API fora.
  baixaApiLote: async (invoiceIds: number[], motivo?: string) =>
    (await api.post("/cnab/baixa-api-lote", { invoiceIds, motivo })).data,
  generateRemessaBaixa: async (invoiceIds: number[]) => {
    const response = await api.post(
      "/cnab/remessa-baixa",
      { invoiceIds },
      { responseType: "blob" },
    );
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    const disposition = response.headers["content-disposition"];
    const filename = disposition
      ? disposition.split("filename=")[1]?.replace(/"/g, "") ||
        "remessa_baixa.rem"
      : "remessa_baixa.rem";
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return { success: true };
  },
  baixaApi: async (invoiceId: number) => {
    return (await api.post("/cnab/baixa-api", { invoiceId })).data;
  },
  // Registra boleto HÍBRIDO (boleto + QR PIX) direto na API do Sicredi
  emitirHibrido: async (invoiceId: number) => {
    return (await api.post("/cnab/emitir-hibrido", { invoiceId })).data;
  },
  // Lote: emite híbridos pra todas as faturas de boleto do mês sem boleto ativo (dryRun = só preview)
  emitirHibridoLote: async (referenceMonth: string, dryRun = false) => {
    // dryRun devolve as candidatas; execução real devolve { jobId } e emite em
    // background (o lote leva minutos e o nginx corta a requisição em 60s).
    return (
      await api.post("/cnab/emitir-hibrido-lote", { referenceMonth, dryRun })
    ).data;
  },
  getLoteJob: async (jobId: number) => {
    return (await api.get("/cnab/emitir-hibrido-lote/" + jobId)).data;
  },
  processRetorno: async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return (
      await api.post("/cnab/retorno", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    ).data;
  },
  getBoletos: async (
    referenceMonth: string,
    startDate?: string,
    endDate?: string,
  ) =>
    (
      await api.get("/cnab/boletos", {
        params: {
          referenceMonth: referenceMonth || undefined,
          ...(startDate && endDate ? { startDate, endDate } : {}),
        },
      })
    ).data,
};

// Conciliação contra a fonte autoritativa do Sicredi (rede de segurança do
// webhook) e situação de saúde da integração.
export const sicrediService = {
  getDivergencias: async () => (await api.get("/sicredi/divergencias")).data,
  // "Atualizar agora" do card da conta: sem datas, o backend usa a janela do cron (hoje e 2 dias atrás). ADMIN; grava.
  sincronizarExtrato: async () => (await api.post("/sicredi/extrato/sincronizar", {})).data,
  // dia em DD/MM/YYYY; dryRun=true só relata, não dá baixa.
  conciliar: async (dia?: string, dryRun = true) =>
    (
      await api.post("/sicredi/conciliacao/executar", null, {
        params: { dia, dryRun: String(dryRun) },
      })
    ).data,
};

// === FINANCIAL MODULE ===
export const expenseCategoriesService = {
  getAll: async () => (await api.get("/expense-categories")).data,
  getById: async (id: number) =>
    (await api.get("/expense-categories/" + id)).data,
  create: async (data: any) =>
    (await api.post("/expense-categories", data)).data,
  update: async (id: number, data: any) =>
    (await api.put("/expense-categories/" + id, data)).data,
  delete: async (id: number) =>
    (await api.delete("/expense-categories/" + id)).data,
  getBudget: async (id: number, month: string) =>
    (
      await api.get("/expense-categories/" + id + "/budget", {
        params: { month },
      })
    ).data,
  setBudget: async (
    id: number,
    data: { month: string; budgetAmount: number },
  ) => (await api.post("/expense-categories/" + id + "/budget", data)).data,
};

export const suppliersService = {
  getAll: async (params?: { page?: number; limit?: number; search?: string }) =>
    (await api.get("/suppliers", { params })).data,
  getById: async (id: number) => (await api.get("/suppliers/" + id)).data,
  create: async (data: any) => (await api.post("/suppliers", data)).data,
  update: async (id: number, data: any) =>
    (await api.put("/suppliers/" + id, data)).data,
  delete: async (id: number) => (await api.delete("/suppliers/" + id)).data,
};

/**
 * Aplicações financeiras (fundos). A conta do fundo é um bank_account com
 * accountType = 'investimento', filho da conta corrente que o alimenta.
 */
export const investmentsService = {
  /** Saldo, rendimento e — o que importa antes de resgatar — quanto está livre de IOF. */
  getSummary: async (accountId: number, date?: string) =>
    (
      await api.get("/investments/accounts/" + accountId + "/summary", {
        params: date ? { date } : {},
      })
    ).data,
  getApplications: async (accountId: number) =>
    (await api.get("/investments/accounts/" + accountId + "/applications"))
      .data,
  getRedemptions: async (accountId: number) =>
    (await api.get("/investments/accounts/" + accountId + "/redemptions")).data,
  getPositions: async (accountId: number) =>
    (await api.get("/investments/accounts/" + accountId + "/positions")).data,
  createApplication: async (data: any) =>
    (await api.post("/investments/applications", data)).data,
  /** Simula sem gravar: quanto de IOF e IRRF um resgate custaria nesta data. */
  planRedemption: async (data: any) =>
    (await api.post("/investments/redemptions/plan", data)).data,
  createRedemption: async (data: any) =>
    (await api.post("/investments/redemptions", data)).data,
  savePosition: async (data: any) =>
    (await api.post("/investments/positions", data)).data,
  deleteApplication: async (id: number) =>
    (await api.delete("/investments/applications/" + id)).data,
  deleteRedemption: async (id: number) =>
    (await api.delete("/investments/redemptions/" + id)).data,
  /** Prévia do PDF "Extrato de aplicação": diz o que seria gravado, sem gravar. */
  previewStatement: async (accountId: number, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return (
      await api.post(
        "/investments/accounts/" + accountId + "/statement/preview",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      )
    ).data;
  },
  importStatement: async (accountId: number, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return (
      await api.post(
        "/investments/accounts/" + accountId + "/statement/import",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      )
    ).data;
  },
};

export const bankAccountsService = {
  getAll: async () => (await api.get("/bank-accounts")).data,
  getById: async (id: number) => (await api.get("/bank-accounts/" + id)).data,
  create: async (data: any) => (await api.post("/bank-accounts", data)).data,
  update: async (id: number, data: any) =>
    (await api.put("/bank-accounts/" + id, data)).data,
  delete: async (id: number) => (await api.delete("/bank-accounts/" + id)).data,
  importOfx: async (id: number, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return (
      await api.post("/bank-accounts/" + id + "/import-ofx", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    ).data;
  },
  importFile: async (id: number, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return (
      await api.post("/bank-accounts/" + id + "/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    ).data;
  },
  getTransactions: async (
    id: number,
    params?: {
      startDate?: string;
      endDate?: string;
      isReconciled?: boolean;
      page?: number;
      limit?: number;
    },
  ) =>
    (await api.get("/bank-accounts/" + id + "/transactions", { params })).data,
  getBalanceHistory: async (id: number) =>
    (await api.get("/bank-accounts/" + id + "/balance-history")).data,
  balanceCheck: async (id: number) =>
    (await api.get("/bank-accounts/" + id + "/balance-check")).data,
  // As linhas de aplicação/resgate do fundo de varredura na conta CORRENTE, como o fundSweep as lê. GET: não grava.
  previaVarreduras: async (contaCorrenteId: number) =>
    (await api.get("/bank-accounts/" + contaCorrenteId + "/varreduras-fundo/previa")).data,
  // Bank Statements (Extrato Oficial)
  uploadStatement: async (
    accountId: number,
    file: File,
    referenceMonth: string,
  ) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("referenceMonth", referenceMonth);
    return (
      await api.post("/bank-accounts/" + accountId + "/statement", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    ).data;
  },
  getStatements: async (accountId: number) =>
    (await api.get("/bank-accounts/" + accountId + "/statements")).data,
  getStatementUrl: async (statementId: number) =>
    (await api.get("/bank-accounts/statements/" + statementId + "/url")).data,
  deleteStatement: async (statementId: number) =>
    (await api.delete("/bank-accounts/statements/" + statementId)).data,
};

export const expensesService = {
  getAll: async (params?: {
    page?: number;
    limit?: number;
    month?: string;
    startDate?: string;
    endDate?: string;
    categoryId?: number;
    supplierId?: number;
    status?: string;
    bankAccountId?: number;
    clientId?: number;
    includeChildren?: boolean;
    search?: string;
  }) => (await api.get("/expenses", { params })).data,
  getById: async (id: number) => (await api.get("/expenses/" + id)).data,
  create: async (data: any) => (await api.post("/expenses", data)).data,
  update: async (id: number, data: any) =>
    (await api.put("/expenses/" + id, data)).data,
  pay: async (
    id: number,
    data: { paymentDate: string; paymentMethod?: string },
  ) => (await api.put("/expenses/" + id + "/pay", data)).data,
  cancel: async (id: number) =>
    (await api.put("/expenses/" + id + "/cancel")).data,
  split: async (
    id: number,
    data: {
      items: {
        description: string;
        amount: number;
        dueDate?: string;
        bankAccountId?: number;
        categoryId?: number;
      }[];
    },
  ) => (await api.post("/expenses/" + id + "/split", data)).data,
  rebalance: async (id: number, firstInstallmentAmount: number) =>
    (
      await api.post("/expenses/" + id + "/rebalance", {
        firstInstallmentAmount,
      })
    ).data,
  replicate: async (id: number, untilMonth: string) =>
    (await api.post("/expenses/" + id + "/replicate", { untilMonth })).data,
  deleteWithFuture: async (id: number) =>
    (await api.delete("/expenses/" + id + "/with-future")).data,
  // cascade=true apaga a subárvore inteira. Sem ele o backend devolve 409 quando
  // a despesa tem lançamentos dentro, em vez de soltá-los como despesas avulsas.
  delete: async (id: number, opts?: { cascade?: boolean }) =>
    (
      await api.delete(
        "/expenses/" + id + (opts?.cascade ? "?cascade=true" : ""),
      )
    ).data,
  summary: async (month?: string, startDate?: string, endDate?: string) =>
    (
      await api.get("/expenses/summary", {
        params: {
          ...(month ? { month } : {}),
          ...(startDate && endDate ? { startDate, endDate } : {}),
        },
      })
    ).data,
  cashFlow: async (months?: number, granularity?: "daily" | "monthly") =>
    (
      await api.get("/expenses/cash-flow", {
        params: {
          ...(months ? { months } : {}),
          ...(granularity ? { granularity } : {}),
        },
      })
    ).data,
  generateRecurring: async (month?: string) =>
    (await api.post("/expenses/generate-recurring", { month })).data,
  uploadReceipt: async (id: number, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return (
      await api.post("/expenses/" + id + "/upload-receipt", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    ).data;
  },
  getReceiptUrl: async (id: number) =>
    (await api.get("/expenses/" + id + "/receipt-url")).data,
  importCsv: async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return (
      await api.post("/expenses/import-csv", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    ).data;
  },
  group: async (data: {
    description: string;
    childIds: number[];
    categoryId?: number;
    supplierId?: number;
    bankAccountId?: number;
    paymentMethod?: string;
  }) => (await api.post("/expenses/group", data)).data,
  getChildren: async (id: number) =>
    (await api.get("/expenses/" + id + "/children")).data,
  getRecurringSiblings: async (id: number) =>
    (await api.get("/expenses/" + id + "/recurring-siblings")).data,
  applyDueDateShift: async (
    id: number,
    data: {
      targetIds: number[];
      newDay: number;
      updateRecurringTemplate?: boolean;
    },
  ) => (await api.post("/expenses/" + id + "/apply-due-date-shift", data)).data,
  getImportTemplate: async () =>
    (await api.get("/expenses/import-csv/template")).data,
  getDreReport: async (startMonth: string, endMonth: string) =>
    (
      await api.get("/expenses/report/dre", { timeout: PRAZO_DE_ARQUIVO_MS,
        params: { startMonth, endMonth },
        responseType: "blob",
      })
    ).data,
  getDreData: async (startMonth: string, endMonth: string) =>
    (
      await api.get("/expenses/report/dre-data", {
        params: { startMonth, endMonth },
      })
    ).data,
  getAnalyticalFlow: async (startMonth: string, endMonth: string) =>
    (
      await api.get("/expenses/report/analytical", { timeout: PRAZO_DE_ARQUIVO_MS,
        params: { startMonth, endMonth },
      })
    ).data,
  getRevenueBySegment: async (
    startMonth: string,
    endMonth: string,
  ): Promise<{
    segments: {
      segment: string;
      total: number;
      clients: number;
      invoices: number;
    }[];
    total: number;
  }> =>
    (
      await api.get("/expenses/report/revenue-by-segment", {
        params: { startMonth, endMonth },
      })
    ).data,
  getBudgetVsActual: async (month: string) =>
    (await api.get("/expenses/budget-vs-actual", { params: { month } })).data,
  getPartnerDeductions: async (partnerId: number, month?: string) =>
    (
      await api.get("/expenses/partner-deductions", {
        params: { partnerId, month },
      })
    ).data,
  getForecast: async (from: string, to: string) =>
    (await api.get("/expenses/forecast", { params: { from, to } })).data,
  addPayment: async (
    id: number,
    data: {
      amount: number;
      paymentDate: string;
      paymentMethod?: string;
      bankTransactionId?: number;
      notes?: string;
    },
  ) => (await api.post("/expenses/" + id + "/payments", data)).data,
  getPayments: async (id: number) =>
    (await api.get("/expenses/" + id + "/payments")).data,
  removePayment: async (paymentId: number) =>
    (await api.delete("/expenses/payments/" + paymentId)).data,
  getAttachments: async (id: number) =>
    (await api.get("/expenses/" + id + "/attachments")).data,
  uploadAttachment: async (
    id: number,
    file: File,
    type: string,
    description?: string,
  ) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", type);
    if (description) formData.append("description", description);
    return (
      await api.post("/expenses/" + id + "/attachments", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    ).data;
  },
  getAttachmentUrl: async (expenseId: number, attachmentId: number) =>
    (
      await api.get(
        "/expenses/" + expenseId + "/attachments/" + attachmentId + "/url",
      )
    ).data,
  removeAttachment: async (expenseId: number, attachmentId: number) =>
    (
      await api.delete(
        "/expenses/" + expenseId + "/attachments/" + attachmentId,
      )
    ).data,
  updateAttachmentType: async (
    expenseId: number,
    attachmentId: number,
    type: string,
  ) =>
    (
      await api.put(
        "/expenses/" + expenseId + "/attachments/" + attachmentId + "/type",
        { type },
      )
    ).data,
  exportExcel: async (month?: string, status?: string) =>
    (
      await api.get("/expenses/export", { timeout: PRAZO_DE_ARQUIVO_MS,
        params: { month, status },
        responseType: "blob",
      })
    ).data,
  parseCardStatement: async (file: File, bank?: string) => {
    const formData = new FormData();
    formData.append("file", file);
    if (bank && bank !== "auto") formData.append("bank", bank);
    return (
      await api.post("/expenses/card-statement/parse", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    ).data;
  },
  importCardStatement: async (file: File, data: any) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("data", JSON.stringify(data));
    return (
      await api.post("/expenses/card-statement/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    ).data;
  },
  getCardStatementRules: async () =>
    (await api.get("/expenses/card-statement/rules")).data,
  createCardStatementRule: async (data: any) =>
    (await api.post("/expenses/card-statement/rules", data)).data,
  updateCardStatementRule: async (id: number, data: any) =>
    (await api.put("/expenses/card-statement/rules/" + id, data)).data,
  deleteCardStatementRule: async (id: number) =>
    (await api.delete("/expenses/card-statement/rules/" + id)).data,
  reapplyCardStatementRules: async (onlyMissing: boolean) =>
    (await api.post("/expenses/card-statement/rules/reapply", { onlyMissing })).data,
};

export const recurringExpensesService = {
  getAll: async () => (await api.get("/recurring-expenses")).data,
  getById: async (id: number) =>
    (await api.get("/recurring-expenses/" + id)).data,
  create: async (data: any) =>
    (await api.post("/recurring-expenses", data)).data,
  update: async (id: number, data: any) =>
    (await api.put("/recurring-expenses/" + id, data)).data,
  delete: async (id: number) =>
    (await api.delete("/recurring-expenses/" + id)).data,
  generateRange: async (data: {
    fromMonth: string;
    toMonth: string;
    recurringExpenseId?: number;
  }) => (await api.post("/recurring-expenses/generate-range", data)).data,
};

export const bankTransactionsService = {
  getAll: async (params?: {
    bankAccountId?: number;
    startDate?: string;
    endDate?: string;
    isReconciled?: boolean;
    page?: number;
    limit?: number;
  }) => (await api.get("/bank-transactions", { params })).data,
  reconcile: async (
    id: number,
    data: {
      expenseId?: number;
      invoiceId?: number;
      fiscalDocumentIds?: number[];
    },
  ) => (await api.put("/bank-transactions/" + id + "/reconcile", data)).data,
  unreconcile: async (id: number) =>
    (await api.put("/bank-transactions/" + id + "/unreconcile")).data,
  // O que o unreconcile faria, sem fazer. Num crédito de lote do Sicredi a ação
  // mexe em dezenas de faturas, e o confirm precisa dizer isso.
  unreconcilePreview: async (
    id: number,
  ): Promise<{
    transactionId: number;
    paymentsToDelete: number;
    paymentsToUnlink: number;
    invoicesAffected: number;
    invoiceCodes: string[];
    expenseWillBeDeleted: boolean;
    expenseDescription: string | null;
  }> =>
    (await api.get("/bank-transactions/" + id + "/unreconcile-preview")).data,
  autoReconcile: async (accountId: number) =>
    (await api.post("/bank-transactions/auto-reconcile/" + accountId)).data,
  suggestMatches: async (id: number) =>
    (await api.get("/bank-transactions/" + id + "/suggest-matches")).data,
  /** Fornecedor que o extrato já nomeia (CNPJ, documento ou nome) + categoria padrão dele. */
  identifyCounterparty: async (
    id: number,
  ): Promise<{
    supplierId: number | null;
    supplierName: string | null;
    categoryId: number | null;
    categoryName: string | null;
    matchedBy: "cnpj" | "documento" | "nome" | null;
    confident: boolean;
  }> =>
    (await api.get("/bank-transactions/" + id + "/identify-counterparty")).data,
  reconcilePartial: async (
    id: number,
    data: {
      expenseId?: number;
      invoiceId?: number;
      amount?: number;
      fiscalDocumentIds?: number[];
    },
  ) =>
    (await api.post("/bank-transactions/" + id + "/reconcile-partial", data))
      .data,
  createExpenseAndReconcile: async (
    id: number,
    data: {
      description: string;
      categoryId: number;
      supplierId?: number;
      bankAccountId?: number;
      paymentMethod?: string;
      notes?: string;
      parentExpenseId?: number;
      createRecurring?: boolean;
      recurringDayOfMonth?: number;
    },
  ) =>
    (
      await api.post(
        "/bank-transactions/" + id + "/create-expense-and-reconcile",
        data,
      )
    ).data,
  // `allocations` rateia o depósito entre as faturas (pagamento parcial de
  // cada uma); sem ele, cada fatura selecionada é quitada pelo saldo inteiro.
  // `fiscalDocumentIds` grava o pagamento contra as NFs que o empenho cobre — o
  // tipo omitia esse campo e só funcionava porque objeto vindo de `.map` escapa
  // do excess property check do TS. Quem "arrumasse" o tipo removeria a
  // associação NF↔pagamento sem perceber. O backend já o declara
  // (bank-transactions.controller.ts:165).
  reconcileMultiInvoice: async (
    id: number,
    invoiceIds?: number[],
    allocations?: Array<{
      invoiceId: number;
      amount: number;
      fiscalDocumentIds?: number[];
    }>,
  ) =>
    (
      await api.post(
        "/bank-transactions/" + id + "/reconcile-multi-invoice",
        allocations?.length ? { allocations } : { invoiceIds },
      )
    ).data,
  bulkReconcileWithExpense: async (
    transactionIds: number[],
    parentExpenseId: number,
  ) =>
    (
      await api.post("/bank-transactions/bulk-reconcile-expense", {
        transactionIds,
        parentExpenseId,
      })
    ).data,
  searchInvoices: async (q: string) =>
    (await api.get("/bank-transactions/search-invoices", { params: { q } }))
      .data,
  searchExpenses: async (q: string, bankAccountId?: number, month?: string) =>
    (
      await api.get("/bank-transactions/search-expenses", {
        params: { q, bankAccountId, month },
      })
    ).data,
  // Fila de trabalho: dinheiro no extrato sem documento do outro lado. Não
  // aparece nas pendências porque `isReconciled` está true em 100% da tabela.
  withoutCounterparty: async (
    bankAccountId?: number,
  ): Promise<
    Array<{
      id: number;
      date: string;
      description: string;
      amount: number;
      type: "credit" | "debit";
      category: string | null;
      bankAccountId: number;
      reason: "boleto_sem_fatura" | "sem_categoria";
    }>
  > =>
    (
      await api.get("/bank-transactions/without-counterparty", {
        params: { bankAccountId },
      })
    ).data,
  ignore: async (id: number) =>
    (await api.put("/bank-transactions/" + id + "/ignore")).data,
  toggleIgnoreAutoReconcile: async (id: number) =>
    (
      await api.put(
        "/bank-transactions/" + id + "/toggle-ignore-auto-reconcile",
      )
    ).data,
  setNotes: async (id: number, notes: string) =>
    (await api.patch("/bank-transactions/" + id + "/notes", { notes })).data,
  setCategory: async (id: number, category: string) =>
    (await api.patch("/bank-transactions/" + id + "/category", { category }))
      .data,
  delete: async (id: number) =>
    (await api.delete("/bank-transactions/" + id)).data,
  bulkDelete: async (accountId: number) =>
    (await api.delete("/bank-transactions/bulk/" + accountId)).data,
  exportExcel: async (
    bankAccountId: number,
    startDate: string,
    endDate: string,
  ) =>
    (
      await api.get("/bank-transactions/export", { timeout: PRAZO_DE_ARQUIVO_MS,
        params: { bankAccountId, startDate, endDate },
        responseType: "blob",
      })
    ).data,
  exportPdf: async (
    bankAccountId: number,
    startDate: string,
    endDate: string,
  ) =>
    (
      await api.get("/bank-transactions/export-pdf", { timeout: PRAZO_DE_ARQUIVO_MS,
        params: { bankAccountId, startDate, endDate },
        responseType: "blob",
      })
    ).data,
};

export const loanContractsService = {
  getAll: async () => (await api.get("/loan-contracts")).data,
  getById: async (id: number) => (await api.get("/loan-contracts/" + id)).data,
  create: async (data: any) => (await api.post("/loan-contracts", data)).data,
  update: async (id: number, data: any) =>
    (await api.put("/loan-contracts/" + id, data)).data,
  delete: async (id: number) =>
    (await api.delete("/loan-contracts/" + id)).data,
  getSummary: async () => (await api.get("/loan-contracts/summary")).data,
  getSchedule: async (id: number) =>
    (await api.get("/loan-contracts/" + id + "/schedule")).data,
  getContractUrl: async (id: number) =>
    (await api.get("/loan-contracts/" + id + "/contract-url")).data,
  uploadContract: async (id: number, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return (
      await api.post("/loan-contracts/" + id + "/upload-contract", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    ).data;
  },
  syncInstallments: async (id: number) =>
    (await api.post("/loan-contracts/" + id + "/sync-installments")).data,
  reprocessInvoices: async (id: number) =>
    (await api.post("/loan-contracts/" + id + "/reprocess-invoices")).data,
  createBorderoExpenses: async (id: number) =>
    (await api.post("/loan-contracts/" + id + "/create-expenses")).data,
  importBordero: async (bankAccountId: number, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("bankAccountId", String(bankAccountId));
    return (
      await api.post("/loan-contracts/import-bordero", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    ).data;
  },
};

/** Uma leitura da senha de um acesso (backend #532): `uso` normalizado (sem o parâmetro, vale 'mostrar'). */
export interface LeituraDeSenha { id: number; uso: 'copiar' | 'mostrar'; usuario: string; userId: number | null; em: string }
/**
 * Uma linha da prévia do titular (backend #532). `titularProposto: null` = não aplicar (o apply recusa). `suspeito` traz
 * o `motivo` por extenso ("Monitorar é uma FASE…"), que a tela mostra para o Heitor desmarcar.
 */
export interface LinhaDaPreviaDoTitular {
  id: number;
  nomeAtual: string;
  titularAtual: string | null;
  titularProposto: string | null;
  nomeSemTitular: string;
  confianca: 'provavel' | 'suspeito';
  motivo: string | null;
}
export interface PreviaDosTitulares { resumo: string; linhas: LinhaDaPreviaDoTitular[] }

export const procurementService = {
  // Agenda do Google (ADMIN). `publicado: false` e `removido: false` NÃO são
  // falha: a marca vale e o `motivo`/`detalhe` diz por quê. Ver agendaGoogle.ts.
  marcarNaAgenda: async (itemId: number): Promise<AgendaMarcarResposta> =>
    (await api.post("/procurement/items/" + itemId + "/agenda")).data,
  removerDaAgenda: async (itemId: number): Promise<AgendaRemoverResposta> =>
    (await api.delete("/procurement/items/" + itemId + "/agenda")).data,
  agendaDiagnostico: async (): Promise<AgendaDiagnostico> =>
    (await api.get("/procurement/agenda/diagnostico")).data,
  agendaSincronizar: async (): Promise<{ armada: boolean; modo: 'seco' | 'ativo'; aplicados: number; plano: number }> =>
    (await api.post("/procurement/agenda/sincronizar")).data,

  // Searches
  getSearches: async () => (await api.get("/procurement/searches")).data,
  createSearch: async (data: any) =>
    (await api.post("/procurement/searches", data)).data,
  updateSearch: async (id: number, data: any) =>
    (await api.put("/procurement/searches/" + id, data)).data,
  deleteSearch: async (id: number) =>
    (await api.delete("/procurement/searches/" + id)).data,
  executeSearch: async (id: number) =>
    (await api.post("/procurement/searches/" + id + "/execute")).data,
  // "Testar perfil": dry-run das keywords (conta + amostra), sem criar itens
  previewSearch: async (
    id: number,
    overrides?: {
      keywords?: string[];
      excludeKeywords?: string[];
      uf?: string[];
      modalidades?: number[];
    },
  ) =>
    (
      await api.post(
        "/procurement/searches/" + id + "/preview",
        overrides || {},
      )
    ).data as {
      total: number;
      sample: Array<{
        objeto: string;
        orgao: string;
        uf: string;
        municipio: string;
        modalidade: string;
        encerramento: string | null;
        jaNoHub: boolean;
      }>;
    },
  exclusionPreview: async () =>
    (await api.get("/procurement/searches/exclusion-preview")).data as {
      termos: string[];
      items: Array<{
        id: number;
        orgaoNome: string;
        objetoCompra: string;
        portalOrigem: string;
        numeroCompra: string;
        termoQueBateu: string;
        temProposta: boolean;
      }>;
    },
  applyExclusions: async (descartarIds: number[], whitelistIds: number[]) =>
    (
      await api.post("/procurement/searches/apply-exclusions", {
        descartarIds,
        whitelistIds,
      })
    ).data as { descartados: number; whitelisted: number },
  // Contratação direta (fornecedor já nomeado) — não-participável
  contratacaoDiretaPreview: async () =>
    (await api.get("/procurement/contratacao-direta/preview")).data as Array<{
      id: number;
      orgao: string;
      municipio: string;
      objeto: string;
      modo: string;
    }>,
  contratacaoDiretaDiscard: async () =>
    (await api.post("/procurement/contratacao-direta/discard", {})).data as {
      discarded: number;
      ids: number[];
    },
  // PNCP API health — status atual (atualizado cron 5min) + probe manual
  pncpHealth: async () =>
    (await api.get("/procurement/pncp-health")).data as {
      up: boolean;
      lastOkAt: string | null;
      lastCheckAt: string | null;
      lastLatencyMs: number | null;
      lastError: string | null;
      consecutiveFailures: number;
      downSinceMs: number | null;
      avgLatencyMs: number | null;
      downtime24hMs: number;
      /** Tamanho da amostra. Sem ele o card afirmava "24h" sobre poucos minutos. */
      probes24h: number;
      /** Desde quando a amostra existe (ISO). null = nenhuma sonda ainda. */
      primeiraSondaAt?: string | null;
      /** 'banco' = janela real de 24h; 'memoria' = só desde o último restart. */
      probesWindowSource?: 'banco' | 'memoria';
    },
  pncpHealthProbe: async () =>
    (await api.post("/procurement/pncp-health/probe", {})).data as {
      ok: boolean;
      latencyMs: number | null;
      error?: string;
    },
  // Items
  getItems: async (params?: any) => {
    const query = params
      ? new URLSearchParams(
          Object.entries(params).filter(
            ([, v]) => v !== undefined && v !== "" && v !== null,
          ) as [string, string][],
        ).toString()
      : "";
    return (await api.get("/procurement/items" + (query ? "?" + query : "")))
      .data;
  },
  getItem: async (id: number) =>
    (await api.get("/procurement/items/" + id)).data,
  getItemDetails: async (id: number, force = false) =>
    (
      await api.get(
        "/procurement/items/" + id + "/details" + (force ? "?force=1" : ""),
      )
    ).data,
  updateItemStatus: async (id: number, data: any) =>
    (await api.patch("/procurement/items/" + id + "/status", data)).data,
  /** Registro MANUAL do desfecho (D-III). O automático vem do PNCP e não se sobrescreve. */
  registrarDesfecho: async (
    id: number,
    desfecho: string,
    observacao?: string,
  ) =>
    (
      await api.patch("/procurement/items/" + id + "/desfecho", {
        desfecho,
        observacao,
      })
    ).data,
  toggleItemMonitored: async (id: number, monitored: boolean) =>
    (await api.patch("/procurement/items/" + id + "/monitored", { monitored }))
      .data,
  refreshItemSituacao: async (id: number) =>
    (await api.post("/procurement/items/" + id + "/refresh-situacao")).data,
  finalizeItemByRanking: async (id: number) =>
    (await api.post("/procurement/items/" + id + "/finalize-by-ranking")).data,
  forceReloginComprasnet: async () =>
    (
      await api.post(
        "/procurement/automation/force-relogin-comprasnet",
        {},
        { timeout: 180000 },
      )
    ).data,
  updateItemPortal: async (id: number, portalOrigem: string) =>
    (await api.patch("/procurement/items/" + id + "/portal", { portalOrigem }))
      .data,
  deleteItem: async (id: number) =>
    (await api.delete("/procurement/items/" + id)).data,
  importFromPncp: async (data: {
    cnpjOrgao?: string;
    ano?: number;
    sequencial?: number;
    url?: string;
  }) => (await api.post("/procurement/items/import-pncp", data)).data,
  registerManual: async (data: {
    portal: string;
    semEdital?: boolean;
    modalidade?: string;
    modoDisputa?: string;
    codigoUnidade?: string;
    numeroCompra?: string;
    orgaoNome?: string;
    objetoCompra?: string;
    valorEstimado?: number;
    dataAbertura?: string;
    dataEncerramento?: string;
    srp?: boolean;
    criterioJulgamento?: string;
    processoSei?: string;
  }) => (await api.post("/procurement/items/register-manual", data)).data,
  updateItemDetails: async (
    id: number,
    data: {
      processoSei?: string;
      criterioJulgamento?: string;
      julgamentoAgrupamento?: "por-lote" | "global" | null;
      modoDisputa?: string;
      orgaoNome?: string;
      objetoCompra?: string;
      dataEncerramento?: string;
      dataAbertura?: string;
      dataSessaoInicio?: string;
      orgaoCnpj?: string;
      codigoUnidade?: string;
      numeroCompra?: string;
    },
  ) => (await api.patch("/procurement/items/" + id + "/details", data)).data,
  lookupUasg: async (uasg: string, numero?: string) =>
    (
      await api.get("/procurement/items/lookup-uasg/" + uasg, {
        params: numero ? { numero } : {},
      })
    ).data,
  // Duplicatas fuzzy cross-portal (Classe B.2)
  getDuplicateSuggestions: async () =>
    (await api.get("/procurement/items/duplicate-suggestions")).data,
  mergeDuplicate: async (id: number, otherId: number, survivorId?: number) =>
    (
      await api.post("/procurement/items/" + id + "/merge-duplicate", {
        otherId,
        survivorId,
      })
    ).data,
  dismissDuplicate: async (id: number, otherId: number) =>
    (
      await api.post("/procurement/items/" + id + "/dismiss-duplicate", {
        otherId,
      })
    ).data,
  // Dashboard & sync
  getDashboard: async () => (await api.get("/procurement/dashboard")).data,
  syncAll: async () =>
    (await api.post("/procurement/sync")).data as {
      message: string;
      startedAt: string;
    },
  syncLastResult: async () =>
    (await api.get("/procurement/sync/last-result")).data as {
      at: string | null;
      totalNew: number;
      searches: number;
      running: boolean;
      error?: string;
      keywords?: string[];
      received?: number;
      inserted?: number;
      updated?: number;
      skipped?: number;
      perKeyword?: Array<{ keyword: string; pagesFetched: number }>;
    },
  resolvePortalLink: async (itemId: number) =>
    (await api.post(`/procurement/items/${itemId}/resolve-portal-link`))
      .data as { link: string | null },
  startReenrich: async () =>
    (await api.post("/procurement/reenrich/start", {})).data as {
      started: boolean;
      running: boolean;
    },
  stopReenrich: async () =>
    (await api.post("/procurement/reenrich/stop", {})).data as {
      stopped: boolean;
    },
  reenrichPendingCount: async () =>
    (await api.get("/procurement/reenrich/pending-count")).data as {
      pending: number;
      withProposal: number;
      processable: number;
      running: boolean;
    },
  reenrichItem: async (id: number) =>
    (
      await api.post(
        `/procurement/items/${id}/reenrich`,
        {},
        { timeout: 55000 },
      )
    ).data as {
      status: string;
      changed: boolean;
      portal?: string;
      numeroCompra?: string;
      dataAbertura?: string | null;
      dataEncerramento?: string | null;
      situacao?: string;
      msg?: string;
    },
  reenrichProgress: async () =>
    (await api.get("/procurement/reenrich/progress")).data as {
      running: boolean;
      total: number;
      processed: number;
      enriched: number;
      errors: number;
      aborted: boolean;
      abortReason?: string;
      finishedAt?: string | null;
      log: Array<{
        id: number;
        orgao: string;
        numero: string;
        status: string;
        portal?: string;
        msg?: string;
      }>;
    },
  // Verificar Vencidos — async com progress (mesmo padrão do Re-enriquecer).
  // Pra items COM proposta enviada cuja sessão venceu há >24h.
  startCheckExpired: async () =>
    (await api.post("/procurement/check-expired/start", {})).data as {
      started: boolean;
      running: boolean;
    },
  stopCheckExpired: async () =>
    (await api.post("/procurement/check-expired/stop", {})).data as {
      stopped: boolean;
    },
  checkExpiredCount: async () =>
    (await api.get("/procurement/check-expired/count")).data as {
      pending: number;
      running: boolean;
    },
  checkExpiredProgress: async () =>
    (await api.get("/procurement/check-expired/progress")).data as {
      running: boolean;
      total: number;
      processed: number;
      transitioned: number;
      unchanged: number;
      errors: number;
      // 24/05/2026 — A3 2-fase: enqueued conta items que monitorItem enviou pro scraper
      // (fire-and-forget, status real virá depois via callback). terminalSinceEnqueue
      // conta callbacks que JÁ chegaram e mudaram status pra terminal.
      enqueued?: number;
      terminalSinceEnqueue?: number;
      enqueuedItemIds?: number[];
      aborted: boolean;
      abortReason?: string;
      finishedAt?: string | null;
      log: Array<{
        id: number;
        orgao: string;
        numero: string;
        status:
          | "transicionado"
          | "inalterado"
          | "erro"
          | "enfileirado"
          | "resolvido_async";
        before?: string;
        after?: string;
        msg?: string;
      }>;
    },
  // 24/05/2026 — A2: tracking de items aguardando callback do scraper
  scraperPending: async () =>
    (await api.get("/procurement/scraper-pending")).data as {
      total: number;
      itemIds: number[];
    },
  // Documents
  getDocuments: async () => (await api.get("/procurement/documents")).data,
  extractDates: async (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return (
      await api.post("/procurement/documents/extract-dates", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    ).data;
  },
  createDocument: async (formData: FormData) =>
    (
      await api.post("/procurement/documents", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    ).data,
  updateDocument: async (id: number, formData: FormData) =>
    (
      await api.put("/procurement/documents/" + id, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    ).data,
  /** Nova versão de UM documento (backend #540): desativa só ele, guarda o S3 e move o link público. 409 `VERSAO_ANTIGA`/`VERSAO_JA_EXISTE`. */
  criarVersaoDoDocumento: async (id: number, formData: FormData) =>
    (
      await api.post("/procurement/documents/" + id + "/versions", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    ).data,
  /** A cadeia (`versoes`, só por previousVersionId) e, à parte, os inativos sem vínculo (`possiveisAnteriores`). */
  versoesDoDocumento: async (id: number) =>
    (await api.get("/procurement/documents/" + id + "/versions")).data,
  deleteDocument: async (id: number) =>
    (await api.delete("/procurement/documents/" + id)).data,
  archiveDocuments: async (ids: number[], archived: boolean) =>
    (await api.patch("/procurement/documents/archive", { ids, archived })).data,
  getDocumentUrl: async (id: number) =>
    (await api.get("/procurement/documents/" + id + "/url")).data,
  createDocumentPublicLink: async (id: number, days?: number) =>
    (await api.post("/procurement/documents/" + id + "/public-link", { days }))
      .data,
  revokeDocumentPublicLink: async (id: number) =>
    (await api.delete("/procurement/documents/" + id + "/public-link")).data,
  getExpiringDocuments: async () =>
    (await api.get("/procurement/documents/expiring")).data,
  getDocumentsWithRenewal: async () =>
    (await api.get("/procurement/documents/with-renewal")).data,
  getRenewalUrls: async () =>
    (await api.get("/procurement/documents/renewal-urls")).data,
  renewCndt: async () =>
    (await api.post("/procurement/documents/renew-cndt")).data,
  renewCndFederal: async () =>
    (await api.post("/procurement/documents/renew-cnd-federal")).data,
  renewCrfFgts: async () =>
    (await api.post("/procurement/documents/renew-crf-fgts")).data,
  renewCndMunicipal: async () =>
    (await api.post("/procurement/documents/renew-cnd-municipal")).data,
  renewCicadMunicipal: async () =>
    (await api.post("/procurement/documents/renew-cicad-municipal")).data,
  renewCndEstadual: async () =>
    (await api.post("/procurement/documents/renew-cnd-estadual")).data,
  renewCicadEstadual: async () =>
    (await api.post("/procurement/documents/renew-cicad-estadual")).data,
  renewCndTcu: async () =>
    (await api.post("/procurement/documents/renew-cnd-tcu")).data,
  renewCndCgu: async () =>
    (await api.post("/procurement/documents/renew-cnd-cgu")).data,
  generateGuiaFalencia: async () =>
    (await api.post("/procurement/documents/generate-guia-falencia")).data,
  renewUpload: async (file: File, type: string) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("type", type);
    return (
      await api.post("/procurement/documents/renew-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    ).data;
  },
  bulkImportDocuments: async (formData: FormData) =>
    (
      await api.post("/procurement/documents/bulk-import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      })
    ).data,
  downloadDocumentsZip: async (category: string) => {
    const response = await api.get("/procurement/documents/download-zip", { timeout: PRAZO_DE_ARQUIVO_MS,
      params: { category },
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${category}.zip`;
    a.click();
    window.URL.revokeObjectURL(url);
  },
  // Products
  getProducts: async () => (await api.get("/procurement/products")).data,
  createProduct: async (data: any) =>
    (await api.post("/procurement/products", data)).data,
  updateProduct: async (id: number, data: any) =>
    (await api.put("/procurement/products/" + id, data)).data,
  deleteProduct: async (id: number) =>
    (await api.delete("/procurement/products/" + id)).data,
  // Portals
  getPortals: async () => (await api.get("/procurement/portals")).data,
  // `uso` (23/10/2026, redesenho de Acessos, S1): o backend registra quem copiou ou mostrou a senha. Backend que ainda não
  // conhece o parâmetro o ignora.
  getPortalPassword: async (id: number, uso?: 'copiar' | 'mostrar') =>
    (await api.get("/procurement/portals/" + id + "/password", { params: uso ? { uso } : undefined })).data,
  // Backend #532 (23/10/2026): quem copiou ou mostrou a senha de um acesso, do mais recente ao mais antigo.
  getPortalPasswordLog: async (id: number, limite?: number): Promise<LeituraDeSenha[]> =>
    (await api.get("/procurement/portals/" + id + "/password-log", { params: limite ? { limite } : undefined })).data,
  // A prévia do titular (dry-run) e o apply dos ids conferidos. O apply não recebe titular: grava o que a prévia mostrou.
  previaDosTitulares: async (): Promise<PreviaDosTitulares> => (await api.get("/procurement/portals/titular/preview")).data,
  aplicarTitulares: async (ids: number[]): Promise<{ aplicados: number; recusados: Array<{ id: number; motivo: string }> }> =>
    (await api.post("/procurement/portals/titular/apply", { ids })).data,
  createPortal: async (data: any) =>
    (await api.post("/procurement/portals", data)).data,
  updatePortal: async (id: number, data: any) =>
    (await api.put("/procurement/portals/" + id, data)).data,
  deletePortal: async (id: number) =>
    (await api.delete("/procurement/portals/" + id)).data,
  // Monitoring
  getMonitoredItems: async () =>
    (await api.get("/procurement/monitoring/items")).data,
  checkMonitoring: async () =>
    (await api.post("/procurement/monitoring/check")).data,
  getBatchStatus: async (batchJobId: string) =>
    (await api.get(`/procurement/monitoring/batch-status/${batchJobId}`)).data,
  getActiveBatch: async () =>
    (await api.get("/procurement/monitoring/active-batch")).data,
  getPortalHealth: async () =>
    (await api.get("/procurement/monitoring/portal-health")).data,
  // A matriz de portais (15/10/2026): o registro de integrações do backend, com a evidência de uso. Só leitura.
  getCapacidadesDosPortais: async () =>
    (await api.get("/procurement/portais/capacidades")).data,
  getPortaisSemLinha: async () =>
    (await api.get("/procurement/portais/sem-linha")).data,
  // GRAVAR ITENS NO PORTAL (Licitanet, contrato do Bastião de 18/10/2026; ver utils/gravacaoNoPortal). Grava e PARA:
  // a confirmação final e os anexos são do Heitor, no portal.
  previaDaGravacaoDeItens: async (proposalId: number) =>
    (await api.get("/procurement/licitanet/gravar-itens/previa?proposalId=" + proposalId)).data,
  /** `confirmarEnvio: true` só depois do sim na tela: na proposta que não existe no portal, é o envio final (sem desfazer). */
  gravarItensNoPortal: async (proposalId: number, marcas?: Record<string, { marca: string; modelo: string }>, opcoes?: { confirmarEnvio: true }) =>
    (await api.post("/procurement/licitanet/gravar-itens", { proposalId, ...(marcas ? { marcas } : {}), ...(opcoes?.confirmarEnvio === true ? { confirmarEnvio: true } : {}) })).data,
  consultarGravacaoDeItens: async (jobId: string) =>
    (await api.get("/procurement/licitanet/gravar-itens/" + encodeURIComponent(jobId))).data,
  getItemLogs: async (id: number) =>
    (await api.get("/procurement/items/" + id + "/logs")).data,
  addItemLog: async (id: number, message: string) =>
    (await api.post("/procurement/items/" + id + "/logs", { message })).data,
  // Metrics
  getMetrics: async () => (await api.get("/procurement/metrics")).data,
  // Proposals
  getProposals: async (params?: any) => {
    const query = params
      ? new URLSearchParams(
          Object.entries(params).filter(
            ([, v]) => v !== undefined && v !== "" && v !== null,
          ) as [string, string][],
        ).toString()
      : "";
    return (
      await api.get("/procurement/proposals" + (query ? "?" + query : ""))
    ).data;
  },
  createProposal: async (formData: FormData) =>
    (
      await api.post("/procurement/proposals", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    ).data,
  updateProposal: async (id: number, formData: FormData) =>
    (
      await api.put("/procurement/proposals/" + id, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    ).data,
  /** Portal sem integração de envio: registra que o cadastro foi feito à mão. */
  marcarPropostaEnviada: async (id: number) =>
    (await api.post("/procurement/proposals/" + id + "/marcar-enviada")).data,
  // "Adequar ao lance final" — SEMPRE dry-run no backend (nada é persistido);
  // retorna rateio + prova de soma + valorAlvoSugerido (fonte: homologação/PNCP/disputa).
  adequarProposta: async (
    id: number,
    body: { valorAlvo?: number; meses?: number },
  ) => (await api.post("/procurement/proposals/" + id + "/adequar", body)).data,
  // F3 checklist de declarações (compute-on-read: edital × templates × gerados)
  getDeclaracoesChecklist: async (id: number) =>
    (await api.get("/procurement/proposals/" + id + "/declaracoes-checklist"))
      .data,
  setChecklistOverride: async (
    id: number,
    body: {
      key: string;
      status?: "atendida_outro" | "nao_aplicavel" | null;
      nota?: string;
      templateId?: number | null;
    },
  ) =>
    (
      await api.patch(
        "/procurement/proposals/" + id + "/checklist-override",
        body,
      )
    ).data,
  deleteProposal: async (
    id: number,
    opts?: {
      destination?: "lixeira" | "encontrar";
      keepMonitored?: boolean;
      keepDispute?: boolean;
    },
  ) => {
    const params = new URLSearchParams();
    if (opts?.destination) params.set("destination", opts.destination);
    if (opts?.keepMonitored) params.set("keepMonitored", "true");
    if (opts?.keepDispute) params.set("keepDispute", "true");
    const qs = params.toString();
    return (
      await api.delete("/procurement/proposals/" + id + (qs ? "?" + qs : ""))
    ).data;
  },
  bulkDeleteProposals: async (
    ids: number[],
    opts?: {
      destination?: "lixeira" | "encontrar";
      keepMonitored?: boolean;
      keepDispute?: boolean;
    },
  ) =>
    (await api.post("/procurement/proposals/bulk-delete", { ids, ...opts }))
      .data,
  bulkArchiveOldProposals: async (olderDays: number = 30) =>
    (await api.post("/procurement/proposals/bulk-archive", { olderDays }))
      .data as { archived: number },
  toggleProposalMonitor: async (id: number, monitorMessages: boolean) =>
    (
      await api.patch("/procurement/proposals/" + id + "/monitor", {
        monitorMessages,
      })
    ).data,
  archiveProposal: async (id: number, archived: boolean) =>
    (await api.patch("/procurement/proposals/" + id + "/archive", { archived }))
      .data,
  getProposalUrl: async (id: number) =>
    (await api.get("/procurement/proposals/" + id + "/url")).data,
  downloadProposalPdf: async (id: number) => {
    const resp = await api.get("/procurement/proposals/" + id + "/pdf", { timeout: PRAZO_DE_ARQUIVO_MS,
      responseType: "blob",
    });
    const blob = new Blob([resp.data], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  },
  // Declaration Templates
  getDeclarationTemplates: async () =>
    (await api.get("/procurement/declaration-templates")).data,
  createDeclarationTemplate: async (data: any) =>
    (await api.post("/procurement/declaration-templates", data)).data,
  updateDeclarationTemplate: async (id: number, data: any) =>
    (await api.put("/procurement/declaration-templates/" + id, data)).data,
  deleteDeclarationTemplate: async (id: number) =>
    (await api.delete("/procurement/declaration-templates/" + id)).data,
  generateDeclarationPdf: async (
    templateId: number,
    itemId: number,
    proposalId?: number,
  ) => {
    const resp = await api.post(
      "/procurement/declaration-templates/generate-pdf",
      { templateId, itemId, proposalId },
      { responseType: "blob" },
    );
    const blob = new Blob([resp.data], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  },
  generateDeclarationZip: async (
    templateIds: number[],
    itemId: number,
    proposalId?: number,
  ) => {
    const resp = await api.post(
      "/procurement/declaration-templates/generate-zip",
      { templateIds, itemId, proposalId },
      { responseType: "blob" },
    );
    const blob = new Blob([resp.data], { type: "application/zip" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `declaracoes-licitacao-${itemId}.zip`;
    a.click();
    URL.revokeObjectURL(url);
  },
  sendDeclarationForSignature: async (data: {
    templateIds: number[];
    itemId: number;
    proposalId?: number;
    signers: Array<{
      name: string;
      email: string;
      cpf?: string;
      birthday?: string;
      authType?: "email" | "icp_brasil" | "whatsapp";
      phone?: string;
    }>;
    mode?: string;
    includePropostaFinanceira?: boolean;
    unifiedTemplateIds?: number[];
    separateTemplateIds?: number[];
    unifiedIncludeProposta?: boolean;
    separateIncludeProposta?: boolean;
  }) =>
    (
      await api.post(
        "/procurement/declaration-templates/send-for-signature",
        data,
      )
    ).data,
  // Assina o PDF que JÁ está no card (baixa do S3 pelo s3Key), sem regenerar pelo
  // template/PDFKit. Único caminho que funciona para documento sem template
  // correspondente e o único que garante assinar o arquivo que aparece na tela.
  sendGeneratedDocForSignature: async (
    docId: number,
    signers: Array<{
      name: string;
      email: string;
      cpf?: string;
      birthday?: string;
      authType?: "email" | "icp_brasil" | "whatsapp";
      phone?: string;
    }>,
  ) =>
    (
      await api.post(
        `/procurement/generated-documents/${docId}/send-for-signature`,
        { signers },
      )
    ).data,
  // Envio em LOTE por ID de documento: assina os PDFs que já estão nos cards, sem
  // regenerar por template. No modo 'unified' o backend mescla os selecionados num
  // pacote e vincula os originais a ele (o checklist continua enxergando cada um).
  sendGeneratedDocsForSignature: async (
    proposalId: number,
    data: {
      documentIds: number[];
      mode?: "separate" | "unified";
      signers: Array<{
        name: string;
        email: string;
        cpf?: string;
        birthday?: string;
        authType?: "email" | "icp_brasil" | "whatsapp";
        phone?: string;
      }>;
    },
  ) =>
    (
      await api.post(
        `/procurement/proposals/${proposalId}/generated-documents/send-for-signature`,
        data,
      )
    ).data,
  // Trocar signatário cancela o documento na ClickSign e recria com o MESMO PDF:
  // remover o único signatário por lá cancela o documento inteiro.
  replaceGeneratedDocSigner: async (
    docId: number,
    signers: Array<{
      name: string;
      email: string;
      cpf?: string;
      birthday?: string;
      authType?: "email" | "icp_brasil" | "whatsapp";
      phone?: string;
    }>,
  ) =>
    (
      await api.post(
        `/procurement/generated-documents/${docId}/replace-signer`,
        { signers },
      )
    ).data,
  // Publicação de documentos para órgão (página pública por licitação)
  getPublications: async (itemId: number) =>
    (await api.get(`/procurement/items/${itemId}/publications`)).data,
  createPublication: async (
    itemId: number,
    data: { title?: string; intro?: string; slug?: string; config?: any },
  ) => (await api.post(`/procurement/items/${itemId}/publications`, data)).data,
  updatePublication: async (
    id: number,
    data: { title?: string; intro?: string; config?: any },
  ) => (await api.put(`/procurement/publications/${id}`, data)).data,
  // Gera os links públicos, monta o dossiê (opcional) e publica a página.
  buildPublication: async (
    id: number,
    data: { linkDays?: number; buildDossier?: boolean },
  ) => (await api.post(`/procurement/publications/${id}/build`, data)).data,
  archivePublication: async (id: number) =>
    (await api.delete(`/procurement/publications/${id}`)).data,
  // Análise IA do Edital
  aiAnalyzerStatus: async () =>
    (await api.get("/procurement/items/ai-analyzer/status")).data as {
      available: boolean;
    },
  aiAnalyzerUsage: async () =>
    (await api.get("/procurement/items/ai-analyzer/usage")).data,

  // AlertaLicitação (agregador externo de licitações)
  alertaLicitacaoUsage: async () =>
    (await api.get("/procurement/alerta-licitacao/usage")).data as {
      total: number;
      bySourceAggregator: Record<string, number>;
      byPortal: Record<string, number>;
      lastSyncAt: string | null;
    },
  // Vocabulário real de modalidades do AlertaLicitação (distinct dos items
  // ingeridos) — alimenta o dropdown do editor de perfil.
  alertaLicitacaoModalidades: async () =>
    (await api.get("/procurement/alerta-licitacao/modalidades")).data as Array<{
      id: number | null;
      nome: string;
      count: number;
    }>,
  getStoredEditalAnalysis: async (itemId: number) =>
    (await api.get(`/procurement/items/${itemId}/ai-analysis`)).data as {
      analysis: any | null;
      analyzedAt: string | null;
    },
  clearEditalAnalysis: async (itemId: number) =>
    (await api.delete(`/procurement/items/${itemId}/ai-analysis`)).data,
  syncItemWithPncp: async (itemId: number) =>
    (
      await api.post(`/procurement/items/${itemId}/sync-pncp`, undefined, {
        timeout: 30000,
      })
    ).data as {
      found: boolean;
      updated: string[];
      item?: any;
      reason?: string;
    },
  analyzeEditalPncp: async (itemId: number, sequencialDocumento?: number) => {
    const url = sequencialDocumento
      ? `/procurement/items/${itemId}/analyze-edital?source=pncp&sequencialDocumento=${sequencialDocumento}`
      : `/procurement/items/${itemId}/analyze-edital?source=pncp`;
    return (await api.post(url, undefined, { timeout: 180000 })).data;
  },
  listEditalFiles: async (itemId: number) =>
    (await api.get(`/procurement/items/${itemId}/edital-files`)).data as Array<{
      sequencialDocumento: number;
      titulo: string;
      tipoDocumentoNome: string;
      tipoDocumentoId: number;
      url: string;
      dataPublicacaoPncp: string;
    }>,
  analyzeEditalUpload: async (itemId: number, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return (
      await api.post(
        `/procurement/items/${itemId}/analyze-edital?source=upload`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 180000,
        },
      )
    ).data;
  },
  generateUnifiedPdf: async (data: {
    templateIds: number[];
    itemId: number;
    proposalId?: number;
    includePropostaFinanceira?: boolean;
  }) => {
    const resp = await api.post(
      "/procurement/declaration-templates/generate-unified-pdf",
      data,
      { responseType: "blob" },
    );
    window.open(
      URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" })),
      "_blank",
    );
  },
  generatePropostaFinanceira: async (itemId: number, proposalId?: number) => {
    const resp = await api.post(
      "/procurement/declaration-templates/generate-proposta-financeira",
      { itemId, proposalId },
      { responseType: "blob" },
    );
    const url = URL.createObjectURL(
      new Blob([resp.data], { type: "application/pdf" }),
    );
    window.open(url, "_blank");
  },
  generateAndSave: async (
    proposalId: number,
    data: { templateIds?: number[]; includePropostaFinanceira?: boolean },
  ) =>
    (
      await api.post(
        `/procurement/proposals/${proposalId}/generate-and-save`,
        data,
      )
    ).data,
  /** Peça técnica (comprovação de exequibilidade, recurso, impugnação). */
  generatePecaTecnica: async (
    proposalId: number,
    data: {
      nome: string;
      titulo: string;
      subtitulo?: string;
      destinatario?: string[];
      secoes: any[];
      incluirDadosContratacao?: boolean;
    },
  ) =>
    (
      await api.post(
        `/procurement/proposals/${proposalId}/generate-peca-tecnica`,
        data,
      )
    ).data,
  getGeneratedDocs: async (proposalId: number) =>
    (await api.get(`/procurement/proposals/${proposalId}/generated-documents`))
      .data,
  getGeneratedDocUrl: async (id: number) =>
    (await api.get(`/procurement/generated-documents/${id}/url`)).data,
  downloadGeneratedDoc: async (id: number): Promise<Blob> =>
    (
      await api.get(`/procurement/generated-documents/${id}/download`, { timeout: PRAZO_DE_ARQUIVO_MS,
        responseType: "blob",
      })
    ).data,
  downloadMergedGeneratedDocs: async (
    ids: number[],
    filename?: string,
  ): Promise<Blob> =>
    (
      await api.post(
        `/procurement/generated-documents/download-merged`,
        { ids, filename },
        { responseType: "blob" },
      )
    ).data,
  deleteGeneratedDoc: async (id: number) =>
    (await api.delete(`/procurement/generated-documents/${id}`)).data,
  getSignatureStatus: async (docId: number) =>
    (await api.get(`/procurement/generated-docs/${docId}/signature-status`))
      .data,
  resendSignatureNotification: async (
    docId: number,
    requestSignatureKey?: string,
  ) =>
    (
      await api.post(
        `/procurement/generated-docs/${docId}/resend-notification`,
        { requestSignatureKey },
      )
    ).data,
  getProposalsWithPendingSignatures: async () =>
    (await api.get("/procurement/proposals/pending-signatures")).data,
  // Competitors
  getCompetitors: async (itemId: number) =>
    (await api.get("/procurement/items/" + itemId + "/competitors")).data,
  syncCompetitors: async (itemId: number) =>
    (await api.post("/procurement/items/" + itemId + "/competitors/sync")).data,
  // Automation
  startAutomation: async (
    proposalId: number,
    portalId: number,
    dryRun: boolean,
  ) =>
    (
      await api.post("/procurement/automation/start", {
        proposalId,
        portalId,
        dryRun,
      })
    ).data,
  // NOTA (23/05/2026): startBllAutomation REMOVIDO (Categoria B). Apontava
  // pro endpoint /automation/bll/start (BLL Chromium legacy), também removido
  // do backend. Substituído por submitProposalBll abaixo.
  //
  // PR2-7 Fase 2 BLL — HTTP-puro via BllProposalService (não usa Chromium).
  // Assíncrono desde PR7: retorna jobId imediato, frontend pollar via
  // getAutomationJob(id) pra ver progresso (mesmo padrão do startAutomation).
  // Checklist pré-envio BLL: status dos docs obrigatórios (upload manual OU
  // gerado+assinado via ponte). Usado no gate do botão de envio.
  getBllDocsStatus: async (proposalId: number) =>
    (await api.get("/procurement/proposals/" + proposalId + "/bll-docs-status"))
      .data as {
      total: number;
      ok: number;
      missing: string[];
      rows: Array<{
        documento: string;
        ok: boolean;
        via: string | null;
        origem: string | null;
      }>;
    },
  /** Desistir no Licitanet: APAGA a proposta inteira no portal. Síncrono; `confirmarDesistencia` só depois do sim na tela. */
  withdrawProposalLicitanet: async (proposalId: number) =>
    (await api.post("/procurement/proposals/" + proposalId + "/withdraw-licitanet", { confirmarDesistencia: true })).data,
  submitProposalBll: async (proposalId: number) =>
    (await api.post("/procurement/proposals/" + proposalId + "/submit-bll"))
      .data as {
      ok: boolean;
      proposalId: number;
      jobId: number;
    },
  withdrawProposalBll: async (proposalId: number) =>
    (await api.post("/procurement/proposals/" + proposalId + "/withdraw-bll"))
      .data as {
      ok: boolean;
      proposalId: number;
      jobId: number;
    },
  // BNC (mesma plataforma do BLL) — envio/desistência de proposta. O checklist
  // de docs (getBllDocsStatus) é portal-agnóstico e serve os dois.
  submitProposalBnc: async (proposalId: number) =>
    (await api.post("/procurement/proposals/" + proposalId + "/submit-bnc"))
      .data as {
      ok: boolean;
      proposalId: number;
      jobId: number;
    },
  withdrawProposalBnc: async (proposalId: number) =>
    (await api.post("/procurement/proposals/" + proposalId + "/withdraw-bnc"))
      .data as {
      ok: boolean;
      proposalId: number;
      jobId: number;
    },
  // NOTA (23/05/2026): scrapeBllEdital REMOVIDO (Categoria B). Apontava pro
  // endpoint legacy /automation/bll/scrape (BLL Chromium). O scrape vivo é
  // bllScrapePublic abaixo (Fase 1 público, HTTP-puro, sem login).
  //
  // Fase 1: scrape público do BLL (HTTP, sem login) — síncrono.
  // `linkUrl` (opcional): o link do processo colado pelo operador. O backend confere a página, busca o token
  // e só então grava (#364). Sem link, o corpo vai vazio, como sempre foi (ver utils/linkDoPortal).
  bllScrapePublic: async (itemId: number, linkUrl?: string) =>
    (await api.post(`/procurement/items/${itemId}/bll-scrape`, corpoDoScrape(linkUrl))).data,
  // BNC (bnccompras.com) — mesma plataforma do BLL, scrape público HTTP-puro.
  bncScrapePublic: async (itemId: number, linkUrl?: string) =>
    (await api.post(`/procurement/items/${itemId}/bnc-scrape`, corpoDoScrape(linkUrl))).data,
  // PCP (Portal de Compras Públicas) — scrape do processo (login + GET
  // RegistroProposta) antes de enviar. Retorna itens/declarações/desempate e
  // persiste os itens. Síncrono.
  pcpScrape: async (itemId: number, pcpUrl?: string): Promise<any> => {
    // timeout alto: na 1ª sync de um processo o gate do edital roda o 2captcha
    // (~30-90s). nginx tem 240s pra essa rota. Depois vira "já realizado" rápido.
    const post = (body: any) =>
      api.post(`/procurement/items/${itemId}/pcp-scrape`, body, {
        timeout: 240000,
      });
    try {
      return (await post(pcpUrl ? { pcpUrl } : {})).data;
    } catch (e: any) {
      // Item descoberto via PNCP/AlertaLicitação não tem a chave operacional do PCP
      // no linkOrigem. Pede o link do processo no PCP e tenta de novo (backend
      // persiste a chave → próxima vez não pergunta).
      const msg = e?.response?.data?.message || "";
      if (!pcpUrl && /ttCD_CHAVE/i.test(msg)) {
        const link = window.prompt(
          "Não achei a chave do processo no PCP automaticamente.\n\n" +
            'Cole o link do processo no Portal de Compras Públicas (a URL dos "Dados do Processo", que contém ttCD_CHAVE=...):',
        );
        if (link && link.trim()) {
          return (await post({ pcpUrl: link.trim() })).data;
        }
      }
      throw e;
    }
  },
  // PCP — envio de proposta (Dispensa + Pregão), fluxo HTTP-puro 5 passos.
  // Assíncrono: retorna jobId imediato, frontend pollar via getAutomationJob.
  submitProposalPcp: async (proposalId: number) =>
    (await api.post("/procurement/proposals/" + proposalId + "/submit-pcp"))
      .data as {
      ok: boolean;
      proposalId: number;
      jobId: number;
    },
  // O formato ENXUTO (backend #491): ver utils/jobDeAutomacao. O detalhe (abaixo) segue completo.
  getAutomationJobs: async (): Promise<JobDaLista[]> =>
    (await api.get("/procurement/automation/jobs")).data,
  getAutomationJob: async (id: number) =>
    (await api.get("/procurement/automation/jobs/" + id)).data,
  // scrapeProposal (POST /procurement/automation/scrape) saiu em 16/10/2026: o backend cauterizou a rota em 27/05/2026
  // (sempre 400 "descontinuado"), e o "tentar de novo" do job ainda a chamava para BLL. A sincronização é a do job
  // (iniciarScrapeDaProposta), pelo sincronizarDoPortal do LicitacoesTab.
  withdrawProposal: async (
    proposalId: number,
    portalId: number,
    dryRun: boolean,
  ) =>
    (
      await api.post("/procurement/automation/withdraw", {
        proposalId,
        portalId,
        dryRun,
      })
    ).data,
  cancelAutomationJob: async (id: number) =>
    (await api.post("/procurement/automation/jobs/" + id + "/cancel")).data,
  linkAutomationJobToProposal: async (jobId: number, proposalId: number) =>
    (
      await api.patch("/procurement/automation/jobs/" + jobId + "/proposal", {
        proposalId,
      })
    ).data,
  uploadBllDocument: async (
    proposalId: number,
    tipo: string,
    obrigatorio: boolean,
    file: File,
  ) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("tipo", tipo);
    formData.append("obrigatorio", obrigatorio ? "true" : "false");
    return (
      await api.post(
        "/procurement/proposals/" + proposalId + "/bll-documents",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      )
    ).data;
  },
  removeBllDocument: async (proposalId: number, tipo: string) =>
    (
      await api.delete(
        "/procurement/proposals/" + proposalId + "/bll-documents",
        { data: { tipo } },
      )
    ).data,
  // Messages
  getItemMessages: async (itemId: number) =>
    (await api.get("/procurement/items/" + itemId + "/messages")).data,
  // Busca o CONTEÚDO do quadro informativo (avisos/esclarecimentos/impugnações) no portal
  refreshQuadroInformativo: async (itemId: number) =>
    (
      await api.post(
        "/procurement/items/" + itemId + "/quadro-informativo/refresh",
      )
    ).data,
  markMessagesRead: async (itemId: number) =>
    (await api.put("/procurement/items/" + itemId + "/messages/read")).data,
  getUnreadMessageCounts: async () =>
    (await api.get("/procurement/messages/unread-counts")).data,
  getRecentConvocacoes: async () =>
    (await api.get("/procurement/convocacoes/recent")).data,
  // 450s nos flows que passam pela fila da extension (scrape/submit/withdraw/
  // delete). Cobre "login frio (~90s, sessão sob-demanda) + espera na fila do
  // Firefox + ação + buffer" e fica ACIMA do teto duro do backend (420s), senão
  // o axios aborta enquanto o backend ainda espera um job que vai dar certo —
  // foi o que aconteceu em 02/09 na dispensa 24/2026 da Câmara de Tremembé: o
  // scrape voltou completo 2 min depois de a tela dizer que falhou.
  scrapeItemNow: async (itemId: number) =>
    (
      await api.post(
        "/procurement/items/" + itemId + "/scrape-now",
        {},
        { timeout: 450000 },
      )
    ).data,
  submitProposalAuto: async (
    itemId: number,
    body: {
      proposalId?: number;
      items?: Array<{ numero: number; valor: number; apelido?: string }>;
      ehMeepp?: boolean;
      dryRun?: boolean;
    },
  ) =>
    (
      await api.post(
        "/procurement/items/" + itemId + "/submit-proposal",
        body,
        { timeout: 450000 },
      )
    ).data,
  withdrawCompraAuto: async (itemId: number) =>
    (
      await api.post(
        "/procurement/items/" + itemId + "/withdraw-compra",
        {},
        { timeout: 450000 },
      )
    ).data,
  deleteItemProposalAuto: async (itemId: number, itemNumero: number) =>
    (
      await api.post(
        "/procurement/items/" + itemId + "/delete-item-proposal/" + itemNumero,
        {},
        { timeout: 450000 },
      )
    ).data,
  // O scrape pelo job (backend #465): o POST devolve o jobId na hora (202) e a tela consulta. Ver utils/scrapeAssincrono.
  iniciarScrapeDaProposta: async (proposalId: number) =>
    (await api.post("/procurement/proposals/" + proposalId + "/scrape-from-portal/async", {})).data,
  consultarJobDeScrape: async (jobId: string) =>
    (await api.get("/procurement/scrape-jobs/" + encodeURIComponent(jobId), { timeout: 15000 })).data,
  getAutomationProgress: async (itemId: number) =>
    (
      await api.get("/procurement/items/" + itemId + "/automation-progress", {
        timeout: 6000,
      })
    ).data,
  getMonitoringStatus: async () =>
    (await api.get("/procurement/monitoring/message-status")).data,
};

// ==================== NOTIFICATIONS ====================
export const notificationsApiService = {
  getAutomations: async () =>
    (await api.get("/notifications/automations")).data,
  updateAutomation: async (key: string, data: any) =>
    (await api.put(`/notifications/automations/${key}`, data)).data,
};

// ==================== EMAIL LOGS ====================
// ── Espelho da tratativa (backend #580/#581; contrato em docs/espelho-da-tratativa-de-reajustes.md do backend). Tudo ADMIN.
export const emailArchiveService = {
  /** Os palpites: e-mail que casou com ALGUM contrato, sem evento e sem descarte. Sem o corpo (vem no detalhe). */
  sugestoes: async (limit = 200, offset = 0) =>
    (await api.get("/email-archive/sugestoes", { params: { limit, offset } })).data,
  /** O arquivo. `semCliente` = os que não casaram com cliente nenhum; `limit: 1` basta para saber se já chegou algum. */
  listar: async (params: { semCliente?: boolean; limit?: number; offset?: number } = {}) =>
    (await api.get("/email-archive", { params: { ...params, semCliente: params.semCliente ? "true" : undefined } })).data,
  /** O e-mail inteiro, com o corpo e os anexos. */
  detalhe: async (id: number) => (await api.get(`/email-archive/${id}`)).data,
  /** URL assinada de um anexo (1 h). */
  anexo: async (id: number, key: string) =>
    (await api.get(`/email-archive/${id}/anexo`, { params: { key } })).data,
  /** Vira evento na linha do tempo. Sem `adjustmentId` = linha do tempo do contrato. `contractId` diferente corrige o palpite. */
  confirmar: async (id: number, body: { contractId?: number | null; adjustmentId?: number | null }) =>
    (await api.post(`/email-archive/${id}/confirmar`, body)).data,
  /** "Não é tratativa". NÃO apaga: o e-mail segue no arquivo. `descartada: false` = já estava. */
  descartar: async (id: number) =>
    (await api.post(`/email-archive/${id}/descartar-sugestao`)).data,
};

export const emailLogsService = {
  getAll: async (params?: any) =>
    (await api.get("/email-logs", { params })).data,
  getStats: async (params?: any) =>
    (await api.get("/email-logs/stats", { params })).data,
  getByEntities: async (entityType: string, entityIds: number[]) =>
    (
      await api.get("/email-logs/by-entities", {
        params: { entityType, entityIds: entityIds.join(",") },
      })
    ).data,
};

// ==================== IMPORT LOGS ====================
export const importLogService = {
  getAll: async (params?: any) =>
    (await api.get("/import-logs", { params })).data,
  getLast: async (importType: string, bankAccountId?: number) =>
    (
      await api.get("/import-logs/last", {
        params: { importType, bankAccountId },
      })
    ).data,
  getLastByAccounts: async (ids: number[]) =>
    (
      await api.get("/import-logs/last-by-accounts", {
        params: { ids: ids.join(",") },
      })
    ).data,
};

// ==================== EC2 MONITORING ====================
export const ec2MonitoringService = {
  getOverview: async () => (await api.get("/ec2-monitoring/overview")).data,
  getMetrics: async (key: string, hours: number = 6) =>
    (await api.get(`/ec2-monitoring/${key}/metrics`, { params: { hours } }))
      .data,
};

// ==================== INFRAESTRUTURA (ComprasNet, VPN, CDP) ====================
export const infrastructureService = {
  getStatus: async () => (await api.get("/admin/infrastructure/status")).data,
  setPncpEgressMode: async (mode: "mac" | "residential" | "vpn" | "direct") =>
    (await api.post("/admin/infrastructure/pncp-egress-mode", { mode }))
      .data as {
      ok: boolean;
      mode?: string;
      residentialAvailable?: boolean;
      error?: string;
    },
  getLightStatus: async () =>
    (await api.get("/admin/infrastructure/light-status")).data,
  forceHeartbeat: async () =>
    (await api.post("/admin/infrastructure/comprasnet/heartbeat")).data,
  forceReloginNew: async () =>
    (
      await api.post(
        "/admin/infrastructure/comprasnet/force-relogin",
        {},
        { timeout: 160000 },
      )
    ).data,
  getClaudeUsage: async () =>
    (await api.get("/admin/infrastructure/claude-usage")).data,
  createManualLoginLock: async (lockedBy?: string, reason?: string) =>
    (
      await api.post("/admin/infrastructure/manual-login/lock", {
        locked_by: lockedBy,
        reason,
      })
    ).data,
  deleteManualLoginLock: async () =>
    (await api.delete("/admin/infrastructure/manual-login/lock")).data,
  pauseComprasnet: async (hours: number, reason?: string) =>
    (
      await api.post("/admin/infrastructure/comprasnet/pause", {
        hours,
        reason,
      })
    ).data,
  unpauseComprasnet: async () =>
    (await api.delete("/admin/infrastructure/comprasnet/pause")).data,
  resetComprasnet: async () =>
    (await api.post("/admin/infrastructure/comprasnet/reset")).data,
  getSessionStats: async (sinceHours = 24) =>
    (
      await api.get("/admin/infrastructure/comprasnet/session-stats", {
        params: { sinceHours },
      })
    ).data,
  getSessionHistory: async (opts?: {
    sinceHours?: number;
    eventType?: string;
    onlyDown?: boolean;
    limit?: number;
    offset?: number;
  }) =>
    (
      await api.get("/admin/infrastructure/comprasnet/session-history", {
        params: opts || {},
      })
    ).data as { total: number; events: any[] },
};

// ==================== PORTABILITY TERMS ====================
export const portabilityTermsService = {
  getAll: async (filters?: {
    ano?: number;
    status?: string;
    clientId?: number;
  }) => {
    const params = new URLSearchParams();
    if (filters?.ano) params.append("ano", String(filters.ano));
    if (filters?.status) params.append("status", filters.status);
    if (filters?.clientId) params.append("clientId", String(filters.clientId));
    const query = params.toString();
    return (await api.get("/portability-terms" + (query ? "?" + query : "")))
      .data;
  },
  getById: async (id: number) =>
    (await api.get("/portability-terms/" + id)).data,
  create: async (data: any) =>
    (await api.post("/portability-terms", data)).data,
  update: async (id: number, data: any) =>
    (await api.patch("/portability-terms/" + id, data)).data,
  delete: async (id: number) =>
    (await api.delete("/portability-terms/" + id)).data,
  emit: async (id: number) =>
    (await api.post("/portability-terms/" + id + "/emit")).data,
  cancel: async (id: number) =>
    (await api.post("/portability-terms/" + id + "/cancel")).data,
  // Revogar é para termo ASSINADO que deixou de valer (motivo obrigatório, 10+ caracteres); cancelar é antes de assinar.
  revoke: async (id: number, motivo: string) =>
    (await api.post("/portability-terms/" + id + "/revoke", { motivo })).data,
  getPdfBase64: async (id: number) =>
    (await api.get("/portability-terms/" + id + "/pdf-base64", { timeout: PRAZO_DE_ARQUIVO_MS })).data,
  getPdf: async (id: number) =>
    (
      await api.get("/portability-terms/" + id + "/pdf", { timeout: PRAZO_DE_ARQUIVO_MS,
        responseType: "blob",
      })
    ).data,
  sendForSignature: async (
    id: number,
    signers: Array<{
      name: string;
      email: string;
      cpf?: string;
      role?: string;
      authType?: string;
    }>,
  ) =>
    (
      await api.post("/portability-terms/" + id + "/send-for-signature", {
        signers,
      })
    ).data,
  getSignatureStatus: async (id: number) =>
    (await api.get("/portability-terms/" + id + "/signature-status")).data,
  resendNotification: async (id: number) =>
    (await api.post("/portability-terms/" + id + "/resend-notification")).data,
  getHistory: async (id: number) =>
    (await api.get("/portability-terms/" + id + "/history")).data,
  getDraftData: async (clientId: number) =>
    (await api.get("/portability-terms/client/" + clientId + "/draft-data"))
      .data,
  getYears: async () => (await api.get("/portability-terms/years")).data,
  getStats: async (ano?: number) =>
    (await api.get("/portability-terms/stats" + (ano ? "?ano=" + ano : "")))
      .data,
};

// ==================== INTEGRATION APPS ====================
export const integrationAppsService = {
  getAll: async () => (await api.get("/integration-apps")).data,
  getOne: async (id: number) => (await api.get("/integration-apps/" + id)).data,
  create: async (data: any) => (await api.post("/integration-apps", data)).data,
  update: async (id: number, data: any) =>
    (await api.patch("/integration-apps/" + id, data)).data,
  revoke: async (id: number, reason?: string) =>
    (await api.post("/integration-apps/" + id + "/revoke", { reason })).data,
  rotateKey: async (id: number) =>
    (await api.post("/integration-apps/" + id + "/rotate-key")).data,
};

// ==================== INVOICE GROUPS ====================
export const invoiceGroupsService = {
  getAll: async () => (await api.get("/invoice-groups")).data,
  getOne: async (id: number) => (await api.get("/invoice-groups/" + id)).data,
  create: async (data: {
    clientId: number;
    contractId?: number;
    description: string;
    items: Array<{
      name: string;
      quantity: number;
      unitValue: number;
      totalValue: number;
      description?: string;
      productId?: number;
      productCode?: string;
    }>;
    totalValue: number;
    installments: number;
    firstDueDate: string;
    intervalDays?: number;
    intervalMonths?: number;
    issueDate?: string;
    periodStart?: string;
    periodEnd?: string;
    paymentMethod?: string;
    notes?: string;
  }) => (await api.post("/invoice-groups", data)).data,
  update: async (
    id: number,
    data: { description?: string; paymentMethod?: string; notes?: string },
  ) => (await api.put("/invoice-groups/" + id, data)).data,
  cancel: async (id: number, reason?: string) =>
    (await api.post("/invoice-groups/" + id + "/cancel", { reason })).data,
  emitNfse: async (id: number) =>
    (await api.post("/invoice-groups/" + id + "/emit-nfse")).data,
  sendEmail: async (
    id: number,
    opts?: { emails?: string[]; customNote?: string },
  ) =>
    (await api.post("/invoice-groups/" + id + "/send-email", opts || {})).data,
  pdfUrl: (id: number) => `${API_URL}/invoice-groups/${id}/pdf`,
  downloadPdf: async (id: number) =>
    (await api.get("/invoice-groups/" + id + "/pdf", { timeout: PRAZO_DE_ARQUIVO_MS, responseType: "blob" }))
      .data,
};

/**
 * Mensagem de erro do backend, inclusive quando a resposta veio como Blob.
 *
 * Com `responseType: 'blob'` (downloads), um 404/500 chega como Blob de JSON e o
 * `err.response.data.message` sai `undefined` — o usuário via só o texto genérico
 * ("Erro ao baixar XMLs") sem nenhuma pista do motivo real.
 */
export async function apiErrorMessage(
  err: any,
  fallback: string,
): Promise<string> {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text())?.message || fallback;
    } catch {
      return fallback;
    }
  }
  return data?.message || fallback;
}

export default api;
